/* vms-slanted-wall-3d.js - the 3D view for the slanted wall page (SLANTED 3D 2026-09-20).
 *
 * WHAT: a "3D view" toggle that draws, BELOW the 2D elevation drawing (owner ruling
 * 2026-09-20: the 3D block sits below the 2D scene, which is never removed), a three.js
 * assembly of the CURRENT slanted-wall build: the angled ceiling plate (two split rigid
 * bodies reassembled at the surface angle), the plumb pipe, the mounting head, the
 * optional 11FC collar, and the translucent simulated TV. There is NO surface of any
 * kind - no ceiling, wall, slab or plane, not even faint (owner verbatim 2026-09-20:
 * "No slanted surface just the parts"). A build whose parts lack a model file reports
 * "not available" and draws nothing.
 *
 * WHY A FORK, NOT A WRAPPER: assets/vms-configurator-3d.js is a closed IIFE bound to the
 * configurator's DOM, build shape and anchor CHAIN. This page has a different DOM
 * (window.VMSSlantedPage, the vmsw- ids), a different build shape (getScene / getBuild),
 * and the plate is posed by a HINGE, not chained by anchor_top. So this file forks the
 * configurator module's proven machinery (the hidden dynamic import(), the show/hide
 * generation token, loadGLB with a url cache, the growing/capped canvas, the projected-
 * corner camera fit, disposeObject, state()) but drives it from computePose, a PURE pose
 * solver. It REUSES assets/vms-three-bundle.js and assets/vms-configurator-3d.css
 * unchanged (identical class names, so the look matches the configurator's 3D view).
 *
 * HOW IT LOADS: a PLAIN classic script (deferred <script src>). three.js is NOT loaded
 * at page load; the bundle (assets/vms-three-bundle.js) is pulled by a dynamic import()
 * on the FIRST toggle only, and no GLB is fetched until then either (zero page-load cost).
 *
 * PUBLIC API (window.VMSSlanted3D): computePose(scene, build, parts) PURE and testable;
 * parseTvOpacity; init(container); show(); hide(); dispose(); state() (a smoke snapshot).
 *
 * [RULE] ES2019 or older here (no optional chaining, no nullish coalescing), EXCEPT the
 *   dynamic import(), which is required and allowed (same as the configurator module).
 * [RULE] ASCII only; no em dash or en dash anywhere (ranges written "X to Y"); the only
 *   non-keyboard glyph the page uses is the degree sign, reused via the DEG_SIGN constant.
 * [RULE] Progressive enhancement: every entry point guards on its globals, so a missing
 *   bundle, a missing VMSSlantedPage or a blocked CDN costs the 3D view and nothing else.
 */
(function () {
  'use strict';

  // ---- constants (inches; the pose math is all in inches, +Y up) -------------
  var IN_TO_M = 0.0254;          // GLBs are in metres; the pose is in inches
  var ASSERT_TOL_IN = 0.02;      // the 0.02 in bar for the plumb-above-hinge residual (spec Tests)
  var TV_ASPECT_W_OVER_H = 16 / 9; // [SLANTED 3D] see tvBox: this page enters SCREEN HEIGHT, not a
                                 //   diagonal, and the 2D SIDE view carries no left-to-right width, so the
                                 //   simulated cabinet width is derived 16:9 from the screen height.
  var DEG_SIGN = '°';       // the degree sign; the only non-keyboard glyph, matching the page
  // [SLANTED 3D, ROUND 3 2026-09-20] The 33CE008HEAD rigid GLB's anchor_top (y = 0) is the
  // SEATED PIPE END, not a physical face: the rigid head mesh still carries the threaded bottom
  // collar (a narrow ring, r about 0.87 to 1.09 in, from y about -0.65 up to +0.35 in), and the
  // head's lower pipe BLOCK (r out to about 2.0 in) begins at y +0.35 in. Pinning the 11FC top
  // to anchor_top leaves 0.35 in of threaded collar showing, which the owner's eye reads as
  // "not mated". INTERIM until the SPLIT head GLB with a collar_seat empty arrives (requested
  // from the renderer 2026-09-20): mate the 11FC top to the head's lower-block UNDERSIDE, this
  // many inches ABOVE anchor_top. MEASURED from the delivered mesh (the rigid 33CE008HEAD.part.glb):
  // the lowest y of head vertices with mast-axis radius 1.3 to 2.2 in over the region y -0.5 to
  // 1.0 in is 0.3500 in, matching the manifest's socket_rim_top_y / collar_top_y 0.35. Replaced by
  // the split head body's collar_seat empty when that GLB arrives (this constant then goes away).
  var HEAD_BLOCK_UNDERSIDE_IN = 0.35;
  // [SLANTED 3D] The 3D scene frame, in inches, right-handed, +Y up:
  //   X3 = the hinge-pin direction (out of the 2D page, toward the side-view camera),
  //   Y3 = up (= the 2D scene's y),
  //   Z3 = ZSIGN * (the 2D scene's horizontal x).
  // The 2D scene's x is positive TOWARD THE ROOM (the TV side); we set ZSIGN = +1 so +Z3
  // points at the room. Viewed from +X3 looking toward -X3 (a right-handed side view, up =
  // +Y3), +Z3 lands on the RIGHT, exactly as the 2D drawing puts the room on the right, so
  // the TV faces the same way relative to the slope as in the 2D drawing. The mast is plumb
  // on the Y axis at X3 = 0, Z3 = 0 (the 2D pipe axis x = 0).
  var ZSIGN = 1;
  // [SLANTED 3D] The plumb-above-hinge residual divides by cos(theta); it degenerates as
  // the surface angle nears 90 degrees. The page's slope handle is capped near 89 degrees
  // and 11CP006 is gated at 83, so cos(theta) never actually reaches 0, but we still report
  // the residual as "not defined" (null) rather than a huge number once cos(theta) is tiny.
  var COS_MIN = 0.02;            // theta > about 88.85 degrees: residual not defined

  // [SLANTED 3D, HEADS SPLIT 2026-09-20] The 33CE008HEAD tilt body's ORIGIN (its store vesa_center,
  // on the pipe axis) displacement per whole degree, in the FIXED file frame (= the rigid
  // 33CE008HEAD.part.glb frame), inches, [dy, dz], +Y up +Z front. From the accepted tilt manifest
  // (origin_displacement_by_deg_in) of 33CE008HEAD.tilt.part.glb, sha256
  // 70fc477c81bf20e2a581aa8a803aaa291e392db13c494a85995bd2bd897cb2d1. This is NOT the page's
  // physical-screen-face displacement (scene.tvPose / the migrating VESA path); the tilt origin
  // rides the pipe axis, so it has its own table. Range 0 to 10; interpolate LINEARLY between whole
  // degrees; clamp outside. Entry 0 is exactly [0, 0].
  var CE008_ORIGIN_DISP = [
    [0, 0], [0.0826, 0.0471], [0.1644, 0.0956], [0.2453, 0.1456], [0.3253, 0.1970],
    [0.4045, 0.2497], [0.4790, 0.3057], [0.5525, 0.3630], [0.6249, 0.4215], [0.6964, 0.4813],
    [0.7668, 0.5424]
  ];
  // ce008Displacement: PURE. Returns the tilt-origin displacement [0, dy, ZSIGN*dz] (inches, scene
  // frame) for a fractional tilt t, linear between whole degrees, clamped to 0 to 10.
  function ce008Displacement(t) {
    var tt = num(t); if (tt === null || tt <= 0) { return [0, 0, 0]; }
    var last = CE008_ORIGIN_DISP.length - 1;   // 10
    if (tt >= last) { return [0, CE008_ORIGIN_DISP[last][0], ZSIGN * CE008_ORIGIN_DISP[last][1]]; }
    var lo = Math.floor(tt), hi = lo + 1, f = tt - lo;
    var dy = CE008_ORIGIN_DISP[lo][0] + f * (CE008_ORIGIN_DISP[hi][0] - CE008_ORIGIN_DISP[lo][0]);
    var dz = CE008_ORIGIN_DISP[lo][1] + f * (CE008_ORIGIN_DISP[hi][1] - CE008_ORIGIN_DISP[lo][1]);
    return [0, dy, ZSIGN * dz];
  }

  function num(v) { return (v === null || v === undefined || v === '' || isNaN(Number(v))) ? null : Number(v); }
  function hasModel(v) { return typeof v === 'string' && v.length > 0; }
  function near(a, b, tol) { return Math.abs(a - b) <= tol; }

  // parseTvOpacity: the data-tv-opacity attribute is a PERCENT string ('30'); return the 0
  // to 1 fraction, clamped, with a 0.30 default for null/''/NaN. Copied verbatim from the
  // configurator module so both pages read the section setting identically. PURE.
  function parseTvOpacity(attrValue) {
    if (attrValue === null || attrValue === undefined || attrValue === '') { return 0.30; }
    var n = Number(attrValue);
    if (isNaN(n)) { return 0.30; }
    n = n / 100;
    if (n < 0) { n = 0; }
    if (n > 1) { n = 1; }
    return n;
  }

  // ---- pure vector helpers for computePose (no DOM, no three.js) --------------
  function rotY(v, deg) {
    // Right-handed rotation about +Y3 by deg. x' = x cos + z sin, z' = -x sin + z cos.
    var a = deg * Math.PI / 180, c = Math.cos(a), s = Math.sin(a);
    return [v[0] * c + v[2] * s, v[1], -v[0] * s + v[2] * c];
  }
  function sub(a, b) { return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]; }

  // [SLANTED 3D] The yaw (about Y3) that rotates a plate's hinge_dir onto +X3 (the pin
  // direction, out of the 2D page). Derived from hinge_dir ALONE (never hardcoded per SKU):
  // yaw = atan2(hinge_dir.z, hinge_dir.x). 11CP006's pin is the file's +Z -> yaw +90 (its
  // whole body turns so the pin lies on X3); ANGLE-CEILING's pin is the file's +X -> yaw 0.
  function yawForPin(hingeDir) {
    var hx = num(hingeDir && hingeDir[0]), hz = num(hingeDir && hingeDir[2]);
    if (hx === null || hz === null) { return 0; }
    return Math.atan2(hz, hx) * 180 / Math.PI;
  }

  // [ROUND 4] PURE 3A-fallback frame check. A pipe product-media GLB has no anchor empties, so
  // synthesise anchor_top / anchor_bottom from its mesh bbox (inches) and validate the frame: the
  // axis must be centred (bbox x and z centre within 0.06 in of the mast axis), the top within
  // 0.06 in of y = 0 (already the pipe-part frame), and the bbox height must equal the part's
  // store length within 0.06 in. Returns { ok, reason, anchorTop, anchorBottom }; a failure gives
  // reason '3a-frame-mismatch'. Exposed for the Node test.
  function synth3aPipe(bbox, storeLen) {
    var cx = (bbox.minX + bbox.maxX) / 2, cz = (bbox.minZ + bbox.maxZ) / 2;
    var topY = bbox.maxY, botY = bbox.minY, heightIn = topY - botY;
    var sl = num(storeLen);
    var axisOk = Math.abs(cx) <= 0.06 && Math.abs(cz) <= 0.06;
    var topOk = Math.abs(topY) <= 0.06;
    var lenOk = sl !== null && Math.abs(heightIn - sl) <= 0.06;
    if (axisOk && topOk && lenOk) {
      return { ok: true, reason: null, anchorTop: [cx, topY, cz], anchorBottom: [cx, botY, cz] };
    }
    return { ok: false, reason: '3a-frame-mismatch',
      axisOk: axisOk, topOk: topOk, lenOk: lenOk, heightIn: heightIn };
  }

  // [ROUND 4] PURE same-length pipe lookup for the extension-kit mapping. Given a pool (sku ->
  // component), a target length and a SKU to exclude, return { url, sku } of a pool pipe/nipple
  // with the same nominal length (within 0.01 in) that has a real model_glb, else { url:'' }.
  function sameLengthPipeUrl(pool, lengthIn, excludeSku) {
    if (!pool || lengthIn === null || lengthIn === undefined) { return { url: '' }; }
    for (var sku in pool) {
      if (!pool.hasOwnProperty(sku) || sku === excludeSku) { continue; }
      var c = pool[sku];
      if (!c || (c.component_type !== 'pipe' && c.component_type !== 'nipple')) { continue; }
      if (num(c.nominal_length) === null) { continue; }
      if (Math.abs(num(c.nominal_length) - lengthIn) <= 0.01 && hasModel(c.model_glb)) {
        return { url: c.model_glb, sku: sku };
      }
    }
    return { url: '' };
  }

  // =========================================================================
  // computePose: PURE. Solves where every drawn object goes, in the 3D scene frame,
  // from getScene(), getBuild() and the per-part anchor data read off the loaded GLBs
  // (plain numbers, inches, each GLB's own local frame). No DOM, no three.js, so Node
  // tests it. See the fixtures in scripts/test_vms_slanted_3d.js.
  //
  // parts (all coordinates INCHES, in the GLB's own local frame; from the manifests):
  //   pipe:  { anchorTop:[x,y,z], anchorBottom:[x,y,z] }              (the plumb mast)
  //   plate: { base:  { hinge:[x,y,z], hingeDir:[x,y,z], rotationSign:+1|-1 },
  //            swivel:{ hinge:[x,y,z], anchorBottom:[x,y,z], hingeDir:[x,y,z] } }
  //   head:  { anchorTop:[x,y,z], vesaCenter:[x,y,z] }
  //   collar:{ anchorTop:[x,y,z] }                                    (11FC, optional)
  // rotationSign is the ONE datum the caller supplies from the base manifest's
  // positive_rotation (prose the module cannot parse). It is +1 or -1 and it, with the
  // hinge_dir-derived yaw, makes the flange lie along the 2D drawing's slope: the room
  // (+Z3) edge of the flange rises with the slope. See VMSSlanted3D placement below.
  //
  // Output: { ok, reason, theta_rad, slopeDeg, tiltDeg, objects:[...], tv:{...},
  //           residuals:{...} }. Each object carries { role, yawDeg, hingeRotDeg, pinAxis,
  //           anchorName, anchorLocal, anchorTarget }: the browser builds the group with
  //           q = Rx(hingeRotDeg) * Ry(yawDeg), reads anchorName's world position, and
  //           translates the group so it lands on anchorTarget. A { ok:false } scene (no
  //           drawable build) yields ok:false and an empty objects[].
  // =========================================================================
  function computePose(scene, build, parts) {
    var out = { ok: false, reason: null, theta_rad: null, slopeDeg: null, tiltDeg: 0,
      objects: [], tv: null, residuals: {} };
    if (!scene || scene.ok !== true) { out.reason = 'no-scene'; return out; }
    parts = parts || {};

    var pitch = num(scene.pitch);
    if (pitch === null || pitch < 0) { out.reason = 'no-pitch'; return out; }
    // theta = atan2(pitch, 12): the page's ONE wall-angle helper (wallThetaRad in
    // vms-slanted-wall.js). slopeDeg from the scene is the SAME angle (pitchDegrees =
    // atan2(pitch, 12) in degrees); we assert that below. Never a second angle formula.
    var theta = Math.atan2(pitch, 12);
    var thetaDeg = theta * 180 / Math.PI;
    out.theta_rad = theta;
    out.slopeDeg = num(scene.slopeDeg);
    out.tiltDeg = num(scene.tiltDeg) || 0;

    var pipeTopY = num(scene.pipeTopY);
    var pipeBotY = num(scene.pipeBotY);
    var holeCentreY = num(scene.holeCentreY);
    if (pipeTopY === null || pipeBotY === null || holeCentreY === null) { out.reason = 'scene-incomplete'; return out; }

    // [SLANTED 3D] The slanted page's mast is ONE straight 1-1/2 inch pipe: a FIXED stock
    // pipe/nipple (mastPiecesForBuild kind 'fixed', vms-slanted-wall.js) or, when the
    // fixed-head height drag reaches a length no stock fixed pipe has, a TELESCOPING unit
    // (kind 'tele', the TELE pool at vms-slanted-wall.js). No couplers, no second piece.
    // Any other pipeKind is not something we know how to draw, so it falls to the plain
    // "not available" sentence rather than a wrong mast.
    var pipeKind = scene.pipeKind || 'fixed';
    if (pipeKind !== 'fixed' && pipeKind !== 'tele') { out.reason = 'unsupported-pipe-kind'; return out; }

    var res = out.residuals;
    // baseRotationDeg is what the tests assert equals slopeDeg (the base rotates by the
    // surface angle). The signed rotation actually applied about the pin is in the object.
    res.baseRotationDeg = thetaDeg;
    res.slopeDegFromScene = out.slopeDeg;
    res.baseRotationEqualsSlope = (out.slopeDeg !== null) && near(thetaDeg, out.slopeDeg, 0.01);

    // ---- the pipe: plumb, anchor_top at scene.pipeTopY (spec item 2, pipe) -----
    var pipe = parts.pipe;
    if (pipe && pipe.anchorTop && pipe.anchorBottom) {
      out.objects.push({
        role: 'pipe', yawDeg: 0, hingeRotDeg: 0, pinAxis: 'x',
        anchorName: 'anchor_top', anchorLocal: pipe.anchorTop.slice(),
        anchorTarget: [0, pipeTopY, 0]
      });
      // Residual: the pipe bottom must land at scene.pipeBotY. With no rotation the pipe
      // origin is (0,pipeTopY,0) - anchorTop, so anchor_bottom lands at
      // pipeTopY - anchorTop.y + anchorBottom.y. The caller reads the pipe anchors AFTER any
      // telescoping extension (placePose lets the tube_inner out to the solved length before
      // reading the anchors, the way the configurator does), so a fixed pipe (anchor span ==
      // its nominal length) and a solved tele (anchor span == the solved length) both close
      // this to ~0; a mismatch reveals a wrong pipe length.
      var pipeBotPlaced = pipeTopY - num(pipe.anchorTop[1]) + num(pipe.anchorBottom[1]);
      res.pipeBottomY = pipeBotPlaced - pipeBotY;
      res.pipePlumb = true; // yaw and hinge are both 0: the mast is plumb on Y by construction
    }

    // ---- the plate: swivel (unrotated) then base (rotated about the pin) -------
    var plate = parts.plate;
    if (plate && plate.base && plate.swivel && plate.base.hinge && plate.swivel.anchorBottom) {
      var sw = plate.swivel, ba = plate.base;
      var yawSw = yawForPin(sw.hingeDir);
      var yawBa = yawForPin(ba.hingeDir);
      // The swivel is placed UNROTATED (about the pin): its anchor_bottom sits on the pipe
      // top (0, pipeTopY, 0). It still carries the pin-aligning YAW (0 for ANGLE-CEILING,
      // 90 for 11CP006), which is a rotation about Y only, so "unrotated" means no rotation
      // about the pin. The hinge point H = pipeTop - Ry(yaw)*swivel.anchor_bottom (from the
      // swivel's own empties; never a retyped constant).
      var swAnchorBottomScene = rotY(sw.anchorBottom, yawSw);
      var H = sub([0, pipeTopY, 0], swAnchorBottomScene);
      out.objects.push({
        role: 'plate_swivel', yawDeg: yawSw, hingeRotDeg: 0, pinAxis: 'x',
        anchorName: 'anchor_bottom', anchorLocal: sw.anchorBottom.slice(),
        anchorTarget: [0, pipeTopY, 0]
      });
      res.swivelRotationDeg = 0; // the swivel never rotates about the pin (asserted by the tests)

      // The base: its hinge lands on H, then it rotates about the pin (now on X3) by the
      // surface angle theta, signed by rotationSign so the flange's room (+Z3) edge rises
      // with the slope (spec item 2, plate base).
      var sign = (num(ba.rotationSign) < 0) ? -1 : 1;
      out.objects.push({
        role: 'plate_base', yawDeg: yawBa, hingeRotDeg: sign * thetaDeg, pinAxis: 'x',
        anchorName: 'hinge', anchorLocal: ba.hinge.slice(),
        anchorTarget: H.slice()
      });

      // Residual (spec Tests, tolerance 0.02 in): the point on the rotated base's mounting-
      // face plane PLUMB ABOVE the hinge must sit at scene y = 0 (the page's P). The base
      // file's mounting face is the y = 0 plane and the hinge sits hinge_to_face below it
      // (perpendicular), so hinge_to_face = |base.hinge.y|. Tilting the face by theta, the
      // point straight up from the hinge meets the face hinge_to_face / cos(theta) above H.
      // (anchor_top is the PERPENDICULAR foot on the face, NOT this plumb-above point, so we
      // pose the base by its hinge and never by anchor_top - director note 2026-09-20.)
      var hingeToFace = Math.abs(num(ba.hinge[1]));
      res.hingeToFace = hingeToFace;
      res.hingeSceneY = H[1];
      if (Math.cos(theta) < COS_MIN) {
        res.plumbAboveHingeY = null;      // degenerate near 90 degrees: not defined
        res.plumbAboveDefined = false;
        res.plumbAboveOk = true;          // not a failure, just undefined at the limit
      } else {
        res.plumbAboveHingeY = H[1] + hingeToFace / Math.cos(theta);
        res.plumbAboveDefined = true;
        res.plumbAboveOk = near(res.plumbAboveHingeY, 0, ASSERT_TOL_IN);
      }
    }

    // ---- the head: SPLIT (fixed + tilt [+ threaded collar]) or RIGID fallback ---
    // headBottomFaceY is the world Y of the head's OWN bottom pipe-entry face (the head GLB's
    // anchor_top empty: for 33CE008HEAD the pipe runs through from the top adapter mouth DOWN
    // to this bottom socket, vesa_center is +6 in above it, so posing vesa on holeCentreY puts
    // anchor_top - the head bottom - below it). The RIGID-path 11FC is placed FROM this, so it
    // cannot drift from the head through a slide (owner bug 2026-09-20).
    var head = parts.head;
    var hf = parts.headFixed, htlt = parts.headTilt, hcol = parts.headCollar;
    var slides = !!scene.slides;
    var tiltDeg = out.tiltDeg;
    var headBottomFaceY = null;
    // [SLANTED 3D, HEADS SPLIT 2026-09-20] Split when both the fixed and the tilt body's empties
    // are present; the rigid model is then not posed. The single-axis heads carry tilt_axis on
    // both bodies; 33CE008HEAD carries collar_seat on the fixed body and no tilt_axis.
    var isSplit = !!(hf && hf.anchorTop && hf.vesaCenter && htlt && htlt.vesaCenter);
    res.headSplit = isSplit;
    res.headTiltDeg = tiltDeg;
    res.pinResidualIn = null;
    res.threadedCollarTopY = null;
    res.collarSeatY = null;

    if (isSplit) {
      // [SLANTED 3D, HEADS SPLIT 2026-09-20] FIXED BODY. A single-axis fixed head hangs its
      // anchor_top on scene.pipeBotY. The SLIDING 33CE008HEAD is posed so the ENGINE'S PHYSICAL hole
      // centre lands on scene.holeCentreY: the 2D engine rests the head with the hole centre
      // HOLE_TO_PIPE_END_RESTING (scene.holeToPipeEndRestingIn = 6.161 in) above the seated pipe end
      // and places the head picture by that physical hole row, NOT by length_offset or by the store
      // vesa_center empty (vms-slope.js [RESTING 2026-08-29]). anchor_top IS the seated pipe end, so
      // anchor_top world Y = holeCentreY - holeToPipeEndRestingIn. Posing by vesa_center (6.0 above
      // anchor_top) instead sat the body 0.161 in too high (director review 2 defect: the rigid file
      // hid this because its threaded collar rode with the head). Fallback to the old vesa pin only
      // if the scene lacks the key (older page). Yaw 0; head local +Z (front) maps to +Z3 (the room).
      var aTopY = num(hf.anchorTop[1]), aTopX = num(hf.anchorTop[0]), aTopZ = num(hf.anchorTop[2]);
      var vcY = num(hf.vesaCenter[1]);
      var hpe = num(scene.holeToPipeEndRestingIn);
      var YtopFixed;
      if (!slides) { YtopFixed = pipeBotY; }
      else if (hpe !== null) { YtopFixed = holeCentreY - hpe; }   // anchor_top (pipe end) lands hpe below the hole centre
      else { YtopFixed = holeCentreY + aTopY - vcY; }   // legacy fallback: pin store vesa_center on holeCentreY
      out.objects.push({
        role: 'head_fixed', yawDeg: 0, hingeRotDeg: 0, pinAxis: 'x',
        anchorName: 'anchor_top', anchorLocal: hf.anchorTop.slice(),
        anchorTarget: [0, YtopFixed, 0]
      });
      headBottomFaceY = YtopFixed;
      // The fixed body places by pure translation (yaw 0): Tf = anchorTarget - anchorTop.
      var Tf = [0 - aTopX, YtopFixed - aTopY, 0 - aTopZ];
      // The FIXED body's store vesa_center world Y. For a fixed single-axis head this equals
      // holeCentreY (headVesaY ~0). For the SLIDING head posed by the physical hole centre it sits
      // hpe - |vesa_center.y| = about 0.161 in BELOW holeCentreY, so headVesaY reads about -0.161:
      // that is the store-vesa vs physical-hole-centre offset, EXPECTED, not a placement error.
      var fixedVesaWorldY = YtopFixed + vcY - aTopY;
      res.headVesaY = fixedVesaWorldY - holeCentreY;
      res.headVesaForwardIn = ZSIGN * num(hf.vesaCenter[2]);
      // collar_seat world Y (33CE008HEAD fixed body): the near-axis block underside the 11FC mates to.
      if (hf.collarSeat) { res.collarSeatY = YtopFixed + num(hf.collarSeat[1]) - aTopY; }

      // TILT BODY.
      if (hf.tiltAxis && htlt.tiltAxis) {
        // Single-axis heads (33HE001, 33HE002, 22HE001; spec item 3): pin the tilt file's tilt_axis
        // empty onto the WORLD position of the fixed file's tilt_axis empty, and rotate the tilt body
        // Rx(+tiltDeg) about X3 through that point. This is the SAME "rotate then pin a named empty on
        // a target" mechanism placeObject gives the plate base (hingeRotDeg); no second placement path,
        // no displacement table. The two tilt_axis empties then coincide (pin residual ~0).
        var faxWorld = [ num(hf.tiltAxis[0]) + Tf[0], num(hf.tiltAxis[1]) + Tf[1], num(hf.tiltAxis[2]) + Tf[2] ];
        out.objects.push({
          role: 'head_tilt', yawDeg: 0, hingeRotDeg: tiltDeg, pinAxis: 'x',
          anchorName: 'tilt_axis', anchorLocal: htlt.tiltAxis.slice(),
          anchorTarget: faxWorld.slice()
        });
        res.fixedTiltAxisWorld = faxWorld.slice();
        res.tiltPinTargetWorld = faxWorld.slice();
        res.pinResidualIn = 0;   // pinned onto the fixed tilt_axis by construction (measured in browser)
      } else {
        // 33CE008HEAD (spec item 4): rotate the tilt file Rx(+tiltDeg) about its OWN origin
        // (vesa_center at the origin), and pin the origin on the fixed file's posed vesa_center world
        // plus the origin displacement D(t) = [0, dy, ZSIGN*dz] (CE008_ORIGIN_DISP, linear, clamped
        // 0 to 10). This is the store-vesa (pipe-axis) displacement, NOT the page's screen-face table.
        var Dt = ce008Displacement(tiltDeg);
        var tgt = [ 0 + Dt[0], fixedVesaWorldY + Dt[1], 0 + Dt[2] ];
        out.objects.push({
          role: 'head_tilt', yawDeg: 0, hingeRotDeg: tiltDeg, pinAxis: 'x',
          anchorName: 'vesa_center', anchorLocal: htlt.vesaCenter.slice(),
          anchorTarget: tgt
        });
        res.tiltPinTargetWorld = tgt.slice();
        res.ce008OriginDisp = Dt.slice();
        res.pinResidualIn = 0;   // vesa_center pinned onto the target by construction
      }

      // THREADED COLLAR (33CE008HEAD; spec item 5): it stays on the END of the pipe at every head
      // position - pin its pipe_end empty on [0, scene.pipeBotY, 0], yaw 0, no rotation. Its top
      // (anchor_top) then sits pipe_end.y below the pin, i.e. at pipeBotY + 0.35 in (the collar's
      // pipe_end is 0.35 below its top). The 2D caps the pipe end IDENTICALLY: threadedTopY =
      // geo.pipeBotY + THREADED_COLLAR_OVER_PIPE_IN (0.35), assets/vms-slanted-wall.js. So the 3D
      // and 2D collar tops agree (residual 0; both are pipeBotY + 0.35). No STOP condition.
      if (hcol && hcol.pipeEnd) {
        out.objects.push({
          role: 'head_collar', yawDeg: 0, hingeRotDeg: 0, pinAxis: 'x',
          anchorName: 'pipe_end', anchorLocal: hcol.pipeEnd.slice(),
          anchorTarget: [0, pipeBotY, 0]
        });
        var aTopColY = hcol.anchorTop ? num(hcol.anchorTop[1]) : 0;
        res.threadedCollarTopY = (pipeBotY - num(hcol.pipeEnd[1])) + aTopColY;
      }
      // [SLANTED 3D, HEADS SPLIT 2026-09-20] restGapIn (director review 2): the fixed body's block
      // underside (collar_seat) minus the threaded collar top. At REST the head bottom sits on the
      // threaded collar, so this is 0; SLID up (11FC on) it equals the slide distance (how far the
      // head rode up the pipe from rest). Reported, never tuned.
      if (res.collarSeatY !== null && res.threadedCollarTopY !== null) {
        res.restGapIn = res.collarSeatY - res.threadedCollarTopY;
      }
    } else if (head && head.anchorTop && head.vesaCenter) {
      // RIGID fallback: a head with an incomplete split set, or a head outside the four. Placed
      // exactly as before (the split path leaves this untouched).
      var raTopY = num(head.anchorTop[1]), rvcY = num(head.vesaCenter[1]);
      if (slides) {
        var Ytop = holeCentreY + raTopY - rvcY;
        headBottomFaceY = Ytop;
        out.objects.push({
          role: 'head', yawDeg: 0, hingeRotDeg: 0, pinAxis: 'x',
          anchorName: 'anchor_top', anchorLocal: head.anchorTop.slice(),
          anchorTarget: [0, Ytop, 0]
        });
        res.headVesaY = (Ytop - raTopY + rvcY) - holeCentreY;
        res.headVesaForwardIn = ZSIGN * num(head.vesaCenter[2]);
      } else {
        headBottomFaceY = pipeBotY;
        out.objects.push({
          role: 'head', yawDeg: 0, hingeRotDeg: 0, pinAxis: 'x',
          anchorName: 'anchor_top', anchorLocal: head.anchorTop.slice(),
          anchorTarget: [0, pipeBotY, 0]
        });
        res.headVesaY = (pipeBotY - raTopY + rvcY) - holeCentreY;
        res.headVesaForwardIn = ZSIGN * num(head.vesaCenter[2]);
      }
    }

    // ---- the 11FC collar (the optional companion), placed FROM THE HEAD ---------
    // Owner bug 2026-09-20: the 11FC must sit mated to the head at every head position.
    //   SPLIT path: pin its anchor_top (top face) on the fixed body's collar_seat world Y (the
    //     mesh-measured near-axis block underside; spec item 6). scene.collarY stays only the
    //     "the 11FC is on this build" signal.
    //   RIGID path: pin its anchor_top on the head's lower-block UNDERSIDE = the head anchor_top
    //     world Y plus the measured HEAD_BLOCK_UNDERSIDE_IN (interim, until the split GLB is used).
    var collar = parts.collar;
    var collarOn = num(scene.collarY) !== null;
    if (isSplit) {
      if (collar && collar.anchorTop && slides && collarOn && res.collarSeatY !== null) {
        out.objects.push({
          role: 'collar', yawDeg: 0, hingeRotDeg: 0, pinAxis: 'x',
          anchorName: 'anchor_top', anchorLocal: collar.anchorTop.slice(),
          anchorTarget: [0, res.collarSeatY, 0]
        });
        res.collarMateGap = 0;                 // 11FC top pinned to the collar_seat plane by construction
        res.collarTopY = res.collarSeatY;
        if (collar.anchorBottom) {
          res.collarBottomY = res.collarSeatY - (num(collar.anchorTop[1]) - num(collar.anchorBottom[1]));
        }
      }
    } else {
      var headBlockUndersideY = (headBottomFaceY !== null) ? (headBottomFaceY + HEAD_BLOCK_UNDERSIDE_IN) : null;
      if (collar && collar.anchorTop && slides && collarOn && headBlockUndersideY !== null) {
        out.objects.push({
          role: 'collar', yawDeg: 0, hingeRotDeg: 0, pinAxis: 'x',
          anchorName: 'anchor_top', anchorLocal: collar.anchorTop.slice(),
          anchorTarget: [0, headBlockUndersideY, 0]
        });
        res.collarMateGap = 0;
        res.collarTopY = headBlockUndersideY;
        res.headBlockUndersideY = headBlockUndersideY;
        if (collar.anchorBottom) {
          res.collarBottomY = headBlockUndersideY - (num(collar.anchorTop[1]) - num(collar.anchorBottom[1]));
        }
      }
    }

    // ---- the TV: translucent box from the scene rectangle + tilt ---------------
    out.tv = tvBox(scene);

    out.ok = true;
    return out;
  }

  // [SLANTED 3D] The simulated TV box, in the 3D scene frame, landed EXACTLY where the 2D
  // draws the TV. The 2D gives the un-rotated side-view rectangle: back at tvBackX (= the
  // head standoff), front at tvFrontX, top tvTopY, bottom tvBotY. In 3D that rectangle is the
  // DEPTH (Z3) by HEIGHT (Y3) face; the box is centred on the pipe axis (X3 = 0) and its
  // width (X3) is derived 16:9 from the screen height (this page has no diagonal and the side
  // view no width, see the deviation note in the report).
  //
  // The 2D does NOT tilt about the hole centre: it tilts about a per-head CAD axis (tiltPivot,
  // tiltAxisDrop, or the migrating tiltTable for 33CE008HEAD) via S.tiltPoint in vms-slope.js,
  // and the drawing reproduces that as "rotate the group about the ORIGINAL hole centre by
  // +tiltDeg, THEN translate by the hole-centre displacement (hcDispX, hcDispY)" (proven equal
  // to tiltPoint for every head kinematics to 1e-9, vms-slanted-wall.js tvTiltTransform). We do
  // the SAME here: rotate the box about the X3 axis through the hole centre by Rx(+tiltDeg),
  // then translate by (Z3 += hcDispX, Y3 += hcDispY). hcDispX/hcDispY arrive on scene.tvPose
  // (the page already computes them; we do not have the per-head axis, so this is the ONE small
  // scene object we depend on). Rx(+t) matches the 2D "clockwise t" rotation in the (room-x=Z3,
  // up=Y3) plane exactly (derivation and proof in scripts/test_vms_slanted_3d.js), so the top of
  // the screen swings toward the room as the 2D draws it. We also emit the four side-view
  // corners so the Node test can assert they equal the tiltPoint-drawn corners within 0.02 in.
  function tvBox(scene) {
    var tvTopY = num(scene.tvTopY), tvBotY = num(scene.tvBotY);
    var tvBackX = num(scene.tvBackX), tvFrontX = num(scene.tvFrontX);
    var holeCentreY = num(scene.holeCentreY);
    var screen = num(scene.screen);
    if (tvTopY === null || tvBotY === null || tvBackX === null || tvFrontX === null ||
        holeCentreY === null || screen === null) { return null; }
    var tp = scene.tvPose || {};
    var hcDispX = num(tp.hcDispX) || 0;      // hole-centre horizontal move (2D room-x = Z3)
    var hcDispY = num(tp.hcDispY) || 0;      // hole-centre vertical move (Y3, + up)
    var h = tvTopY - tvBotY;                 // == screen
    var depth = tvFrontX - tvBackX;          // the 2D panel depth
    var w = num(scene.tvWidth) > 0 ? num(scene.tvWidth) : Math.abs(screen) * TV_ASPECT_W_OVER_H;
    var tiltDeg = num(scene.tiltDeg) || 0;
    var center = [0, (tvTopY + tvBotY) / 2, ZSIGN * (tvBackX + tvFrontX) / 2];
    var pivot = [0, holeCentreY, ZSIGN * tvBackX];

    // The four side-view corners (Z3, Y3) after the transform, matching the 2D draw. Rotate
    // each un-rotated corner about the hole centre by Rx(+tiltDeg) in the (Z3, Y3) plane, then
    // add hcDisp. Order: back-top, front-top, back-bottom, front-bottom.
    var t = tiltDeg * Math.PI / 180, ct = Math.cos(t), st = Math.sin(t);
    var pZ = ZSIGN * tvBackX, pY = holeCentreY;
    function corner(roomx, y) {
      var dZ = ZSIGN * roomx - pZ, dY = y - pY;
      // Rx(+t): dZ' = dY*sin t + dZ*cos t ; dY' = dY*cos t - dZ*sin t.
      var z = pZ + dY * st + dZ * ct + hcDispX;
      var yy = pY + dY * ct - dZ * st + hcDispY;
      return [z, yy];
    }
    var sideCorners = [
      corner(tvBackX, tvTopY), corner(tvFrontX, tvTopY),
      corner(tvBackX, tvBotY), corner(tvFrontX, tvBotY)
    ];
    return {
      w: w, h: h, depth: depth,
      center: center, pivot: pivot,
      tiltDeg: tiltDeg, tiltSign: 1,          // Rx(+tiltDeg), matching the 2D clockwise turn
      hcDisp: [0, hcDispY, hcDispX],          // scene-frame translation after the rotation
      sideCorners: sideCorners
    };
  }

  // =========================================================================
  // Everything below is browser-only. It never runs under the Node harness (which loads
  // this file and exercises computePose / parseTvOpacity against fixtures), because it is
  // gated behind init()/show() and the presence of a DOM plus the three.js bundle.
  // =========================================================================

  var S = {
    wrap: null, container: null, toggle: null, statusEl: null, settings: null,
    pool: null, config: null,
    THREE: null, GLTFLoader: null, OrbitControls: null, loader: null,
    renderer: null, scene: null, camera: null, controls: null, canvas: null,
    root: null, tvMesh: null, tvFrontMat: null, presetsEl: null, calloutsEl: null,
    // [STUDIO ENV 2026-09-22] the prefiltered studio environment (one per renderer)
    // and the measured PMREM build cost in ms.
    envTex: null, envMs: null,
    contentH_in: null, appliedCanvasH: null, curAz: 35, curEl: 15,
    glbCache: {}, glbPromises: {}, groups: [], groupsByRole: {}, ro: null, animId: 0,
    orbiting: false,
    gen: 0, awaitingFrame: false, pendingOpenScroll: false, calloutsMeasured: false,
    visible: false, importing: false, bundleReady: false, built: false, disposed: false,
    firstFrameAt: null, showClickAt: null, tris: 0, lastPose: null, available: false,
    loadedUrls: [], residuals: {}, reposeRaf: 0,
    // [SLANTED 3D, ROUND 3 FIXES 2026-09-20] monotonic re-pose sequence (owner bug A, stacked poses).
    reposeSeq: 0
  };

  // [SLANTED 3D, ROUND 3 FIXES 2026-09-20] PURE guard for the stacked-repose race: a resolved load
  // may place ONLY if its sequence is still the newest; a later repose supersedes an earlier one.
  function reposeSuperseded(mySeq, currentSeq) { return mySeq !== currentSeq; }

  function readSettings(wrap) {
    function d(name, fallback) { var v = wrap.getAttribute(name); return (v === null || v === undefined) ? fallback : v; }
    return {
      bundleUrl: d('data-bundle-url', ''),
      loading: d('data-label-loading', 'Loading the 3D view'),
      unavailable: d('data-label-unavailable', 'The 3D view is not available for this combination yet.'),
      front: d('data-label-front', 'Front'),
      side: d('data-label-side', 'Side'),
      angleLabel: d('data-label-angle', 'Ceiling angle'),
      tiltLabel: d('data-label-tilt', 'Screen tilt'),
      pipeLabel: d('data-label-pipe', 'Pipe length'),
      tv: d('data-label-tv', 'TV'),
      tvOpacity: parseTvOpacity(wrap.getAttribute('data-tv-opacity')),
      toggleOff: d('data-label-toggle-off', '3D view'),
      toggleOn: d('data-label-toggle-on', 'Hide 3D view')
    };
  }

  // The pool is the SAME JSON the calculator reads (#vmscfg-data -> components), keyed by
  // SKU, so the pipe and head model_glb urls are read from it. The plate uses the split
  // files from #vmsw-config instead (see resolveUrls), never the rigid plate model_glb.
  function parsePool() {
    var map = {};
    try {
      var el = document.getElementById('vmscfg-data');
      if (!el) { return map; }
      var json = JSON.parse(el.textContent || '{}');
      var comps = json.components || [];
      for (var i = 0; i < comps.length; i++) {
        var c = comps[i];
        if (c && c.sku) { map[c.sku] = c; }
      }
    } catch (e) { /* a missing or malformed pool just yields "not available" */ }
    return map;
  }

  function parseConfig() {
    try {
      var el = document.getElementById('vmsw-config');
      if (!el) { return {}; }
      return JSON.parse(el.textContent || '{}');
    } catch (e) { return {}; }
  }

  function getScene() {
    try {
      if (window.VMSSlantedPage && typeof window.VMSSlantedPage.getScene === 'function') {
        return window.VMSSlantedPage.getScene();
      }
    } catch (e) { /* ignore */ }
    return { ok: false };
  }
  function getBuild() {
    try {
      if (window.VMSSlantedPage && typeof window.VMSSlantedPage.getBuild === 'function') {
        return window.VMSSlantedPage.getBuild();
      }
    } catch (e) { /* ignore */ }
    return null;
  }

  function setStatus(text) {
    if (!S.statusEl && S.container) { S.statusEl = S.container.querySelector('[data-vmsw-3d-status]'); }
    if (S.statusEl) {
      S.statusEl.textContent = text; S.statusEl.hidden = false;
      // [SLANTED 3D, ROUND 2] The CSS-only loader ring (assets/vms-configurator-3d.css) shows
      // ONLY while the status carries is-loading, i.e. only for the loading text. The
      // not-available / error texts clear it, and hideStatus (first rendered frame) clears it.
      var loading = !!(S.settings && text === S.settings.loading);
      if (loading) { S.statusEl.classList.add('is-loading'); }
      else { S.statusEl.classList.remove('is-loading'); }
    }
  }
  function hideStatus() {
    if (S.statusEl) { S.statusEl.hidden = true; S.statusEl.classList.remove('is-loading'); }
  }

  // ---- the plate rotation sign per SKU, from the split base manifests ---------
  // [SLANTED 3D] positive_rotation is prose the module cannot parse, so this is the ONE
  // per-SKU datum computePose needs supplied. Derived from the accepted manifests +
  // director note 2026-09-20 so the flange's ROOM (+Z3) edge rises with the slope:
  //   11CP006:      +rotation about the +Z pin lifts the +X flange edge. yawForPin maps
  //                 file +Z -> +X3 (yaw +90), under which file +X -> -Z3 (the wall side).
  //                 So +rotation lifts the WALL edge; use -1 to lift the room edge.
  //   ANGLE-CEILING:+rotation about the +X pin lifts the -Z flange edge. yaw is 0, so
  //                 file -Z -> -Z3 (the wall side). So +rotation lifts the wall edge; use
  //                 -1 to lift the room edge.
  // Both resolve to -1 here; kept per-SKU (not a single constant) because the sign follows
  // from each plate's own pin and positive_rotation and a future plate could differ.
  var PLATE_ROTATION_SIGN = { '11CP006': -1, 'ANGLE-CEILING': -1 };

  // ---- init: idempotent, cheap, NO three.js ----------------------------------
  function init(container) {
    if (S.wrap) { return; }
    var wrap = (container && container.closest) ? container.closest('.vmsw-3d-wrap') : document.querySelector('.vmsw-3d-wrap');
    if (!wrap) { return; }
    S.wrap = wrap;
    S.container = wrap.querySelector('#vmsw-elev3d') || wrap.querySelector('.vmscfg-3d');
    S.toggle = wrap.querySelector('#vmsw-elev3dToggle') || wrap.querySelector('.vmscfg-3d__toggle');
    S.statusEl = wrap.querySelector('[data-vmsw-3d-status]');
    S.settings = readSettings(wrap);
    S.pool = parsePool();
    S.config = parseConfig();
    // The wrap ships hidden (so no-JS never shows a dead toggle under the invisible, empty
    // calculator); reveal it now that the script is running.
    if (wrap.hasAttribute('hidden')) { wrap.removeAttribute('hidden'); }
    if (S.toggle) {
      S.toggle.addEventListener('click', function () {
        if (S.visible) { hide(); } else { show(); }
      });
    }
    // [SLANTED 3D] Re-pose on every build render. The page rewrites #vmsw-answer (and the
    // 2D SVG inside it) on each renderAnswer and fires vmsw:rendered on ROOT (#vmsw-calc)
    // with the fresh scene; while the 3D view is open we re-pose. A drag fires many events
    // per second, so coalesce to ONE re-pose per animation frame; loadGLB caches by url so a
    // re-pose never refetches a GLB already loaded.
    // [WARN] The page dispatches vmsw:rendered with bubbles:false, so a plain document-level
    // listener never sees it. We bind on the ROOT element itself (the dispatch target), with
    // a document capture-phase listener as a fallback (the capture phase reaches ancestors
    // even for a non-bubbling event). Same channel the drag layer listens on.
    var onRendered = function () {
      if (!S.visible) { return; }
      if (S.reposeRaf) { return; }
      if (typeof requestAnimationFrame === 'function') {
        S.reposeRaf = requestAnimationFrame(function () { S.reposeRaf = 0; repose(); });
      } else {
        repose();
      }
    };
    var rootEl = document.getElementById('vmsw-calc');
    if (rootEl) { rootEl.addEventListener('vmsw:rendered', onRendered); }
    else { document.addEventListener('vmsw:rendered', onRendered, true); }
  }

  // ---- the bundle: dynamic import() on first show only -----------------------
  // [WARN] Shopify's CDN minifies theme .js and downlevels a syntactic import() to
  // require(), which throws "require is not defined". We keep a REAL dynamic import() but
  // hide it from that minifier inside a Function body; a module <script> is the fallback if
  // a CSP blocks Function (unsafe-eval). Copied from the configurator module (proven live).
  function importBundle(url) {
    try {
      var dyn = new Function('u', 'return import(u);');
      return dyn(url);
    } catch (e) {
      return importViaModuleTag(url);
    }
  }
  function importViaModuleTag(url) {
    return new Promise(function (resolve, reject) {
      var cb = '__vmsw3d_' + Date.now();
      window[cb] = function (m) { try { delete window[cb]; } catch (x) {} resolve(m); };
      var s = document.createElement('script');
      s.type = 'module';
      s.textContent = 'import * as m from ' + JSON.stringify(url) + '; window[' + JSON.stringify(cb) + '](m);';
      s.onerror = function () { reject(new Error('module script failed: ' + url)); };
      document.head.appendChild(s);
      setTimeout(function () { if (window[cb]) { reject(new Error('bundle import timed out: ' + url)); } }, 20000);
    });
  }

  function ensureBundle() {
    if (S.bundleReady) { return Promise.resolve(true); }
    if (S.importing) { return S.importing; }
    if (!S.settings || !S.settings.bundleUrl) { return Promise.reject(new Error('no bundle url')); }
    var pr = importBundle(S.settings.bundleUrl).then(function (mod) {
      S.THREE = mod.THREE; S.GLTFLoader = mod.GLTFLoader; S.OrbitControls = mod.OrbitControls;
      S.loader = new S.GLTFLoader();
      S.bundleReady = true;
      return true;
    });
    pr.catch(function () { if (S.importing === pr) { S.importing = null; } });
    S.importing = pr;
    return pr;
  }

  // ---- show / hide -----------------------------------------------------------
  function show() {
    if (!S.wrap) { return Promise.resolve(false); }
    S.gen++;
    var myGen = S.gen;
    S.showClickAt = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    S.firstFrameAt = null;
    S.visible = true; S.disposed = false;
    if (S.container) { S.container.hidden = false; }
    S.pendingOpenScroll = true;
    if (S.toggle) { S.toggle.setAttribute('aria-pressed', 'true'); S.toggle.textContent = S.settings.toggleOn; }
    setStatus(S.settings.loading);
    return ensureBundle().then(function () {
      if (S.gen !== myGen || !S.visible) { return false; }
      buildRenderer();
      repose(myGen);
      return true;
    }).catch(function (e) {
      if (S.gen !== myGen) { return false; }
      setStatus(S.settings.unavailable);
      if (S.container) { S.container.setAttribute('data-vmsw-3d-error', String(e && e.message ? e.message : e)); }
      if (S.pendingOpenScroll && S.visible) { S.pendingOpenScroll = false; scrollCanvasToTopIfMobile(); }
      return false;
    });
  }

  function hide() {
    S.gen++;
    S.visible = false;
    if (S.container) { S.container.hidden = true; }
    if (S.toggle) { S.toggle.setAttribute('aria-pressed', 'false'); S.toggle.textContent = S.settings.toggleOff; }
    disposeGL();
  }

  // ---- renderer / scene ------------------------------------------------------
  var REF_W_IN = 48;
  function canvasHeight(w, contentH_in, viewportH, calloutsH) {
    var aspect = 0.72;
    if (contentH_in && contentH_in > 0) { aspect = Math.max(0.72, contentH_in / REF_W_IN); }
    var h = Math.round(w * aspect);
    var floor = 340;
    var ceil = 1100;
    if (typeof viewportH === 'number' && viewportH > 0) {
      var ch = (typeof calloutsH === 'number' && calloutsH >= 0) ? calloutsH : 0;
      var hMax = Math.round(viewportH - ch - 16);
      if (hMax < ceil) { ceil = hMax; }
    }
    if (ceil < floor) { ceil = floor; }
    if (h < floor) { h = floor; }
    if (h > ceil) { h = ceil; }
    return h;
  }

  function calloutsHeightPx() {
    if (S.calloutsMeasured && S.calloutsEl) { return S.calloutsEl.offsetHeight; }
    var vw = (typeof window !== 'undefined' && window.innerWidth) ? window.innerWidth : 320;
    return (vw <= 749) ? 56 : 28;
  }

  function containerSize() {
    var w = Math.max(240, Math.floor((S.container && S.container.clientWidth) || 320));
    var vh = (typeof window !== 'undefined' && window.innerHeight) ? window.innerHeight : 0;
    var h = canvasHeight(w, S.contentH_in, vh, calloutsHeightPx());
    return { w: w, h: h };
  }

  function applyCanvasSize() {
    if (!S.renderer || !S.camera) { return; }
    var sz = containerSize();
    S.renderer.setSize(sz.w, sz.h, false);
    S.canvas.style.width = '100%';
    S.canvas.style.height = 'auto';
    S.camera.aspect = sz.w / sz.h;
    S.camera.updateProjectionMatrix();
    S.appliedCanvasH = sz.h;
  }

  function scrollCanvasToTopIfMobile() {
    var doScroll = function () {
      try {
        if (!S.visible || !S.container) { return; }
        var vw = (typeof window !== 'undefined' && window.innerWidth) ? window.innerWidth : 0;
        if (vw <= 0 || vw > 749) { return; }
        var rect = S.container.getBoundingClientRect();
        var pageY = rect.top + (window.scrollY || window.pageYOffset || 0) - (parseFloat(getComputedStyle(document.querySelector('main') || document.body).scrollMarginTop) || 0); // sticky header round 2026-09-28: clear the stuck header
        window.scrollTo(0, Math.max(0, Math.round(pageY)));
      } catch (e) { /* ignore */ }
    };
    if (typeof requestAnimationFrame === 'function') { requestAnimationFrame(function () { requestAnimationFrame(doScroll); }); }
    else { doScroll(); }
  }

  function readBgColor() {
    try {
      var elev = document.getElementById('vmsw-elev-svg');
      if (elev && elev.parentNode) {
        var bg = getComputedStyle(elev.parentNode).backgroundColor;
        if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') { return bg; }
      }
    } catch (e) { /* ignore */ }
    return '#ffffff'; // the 2D slanted drawing paints a white ground
  }

  // ---- [STUDIO ENV 2026-09-22] the procedural studio room ------------------
  // The mount GLBs are "Paint Matte Black" (baseColorFactor 0.0025, metalness 0,
  // roughness 0.46), so nearly everything the customer sees is specular
  // reflection, not diffuse colour. A HemisphereLight contributes NO specular in
  // three.js, which left exactly one hot spot from the key light and a part that
  // reads black. Shopify's model-viewer always carries a default image based
  // environment; this gives our two engines the same kind of fill.
  // three's examples/jsm RoomEnvironment is not in our bundle (core only), so the
  // room is built here: a grey shell plus emissive panels, prefiltered once per
  // renderer by PMREMGenerator and kept on S.envTex. Values are kept identical to
  // assets/vms-configurator-3d.js so both views match.
  var STUDIO_SHELL = 0x08080a;   // the box the panels sit inside
  var STUDIO_TOP = 9.0;          // broad overhead softbox
  var STUDIO_SIDE = 5.0;         // left and right, mid height
  var STUDIO_FRONT = 6.0;        // camera side: the face the customer looks at
  var STUDIO_BACK = 8.5;         // rim from behind
  var STUDIO_ENV_INTENSITY = 0.31;
  var STUDIO_EXPOSURE = 1.0;
  var STUDIO_HEMI = 0.12;        // was 1.05 before the environment carried the fill
  var STUDIO_KEY = 0.49;          // was 1.1; still the highlight source

  function buildStudioEnvironment(THREE, renderer) {
    var room = new THREE.Scene();
    var junk = [];
    // Emissive, not lit: a PMREM scene of ours carries no lights, and a purely
    // lit MeshStandardMaterial would prefilter to black.
    var shellGeo = new THREE.BoxGeometry(10, 10, 10);
    var shellMat = new THREE.MeshStandardMaterial({
      color: STUDIO_SHELL, emissive: STUDIO_SHELL, emissiveIntensity: 1,
      roughness: 1, metalness: 0, side: THREE.BackSide
    });
    room.add(new THREE.Mesh(shellGeo, shellMat));
    junk.push(shellGeo, shellMat);

    function panel(w, h, d, x, y, z, intensity) {
      var geo = new THREE.BoxGeometry(w, h, d);
      var mat = new THREE.MeshBasicMaterial();
      mat.color.setScalar(intensity); // above 1 is legal: the PMREM target is half float
      var mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(x, y, z);
      room.add(mesh);
      junk.push(geo, mat);
    }
    panel(5.0, 0.2, 5.0, 0.0, 4.6, 0.0, STUDIO_TOP);
    panel(0.2, 3.0, 4.0, -4.6, 0.6, 0.0, STUDIO_SIDE);
    panel(0.2, 3.0, 4.0, 4.6, 0.6, 0.0, STUDIO_SIDE);
    panel(4.5, 3.0, 0.2, 0.0, 0.6, 4.6, STUDIO_FRONT);
    panel(4.5, 3.0, 0.2, 0.0, 0.6, -4.6, STUDIO_BACK);

    var pmrem = new THREE.PMREMGenerator(renderer);
    var tex = pmrem.fromScene(room, 0.04).texture;
    pmrem.dispose();
    while (room.children.length) { room.remove(room.children[0]); }
    for (var i = 0; i < junk.length; i++) { try { junk[i].dispose(); } catch (e) { /* ignore */ } }
    return tex;
  }

  // Applies the environment and ACES tone mapping to the scene just built. Called
  // once per renderer; a re-pose or a rebuild never makes a second PMREM.
  function applyStudioEnvironment(THREE) {
    S.envTex = null; S.envMs = null;
    try {
      var t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      S.envTex = buildStudioEnvironment(THREE, S.renderer);
      var t1 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      S.envMs = Math.round((t1 - t0) * 10) / 10;
      S.scene.environment = S.envTex;
      S.scene.environmentIntensity = STUDIO_ENV_INTENSITY;
      S.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      S.renderer.toneMappingExposure = STUDIO_EXPOSURE;
    } catch (e) {
      // Never let a lighting nicety stop the view drawing.
      S.envTex = null; S.envMs = null;
      try { S.scene.environment = null; } catch (e2) { /* ignore */ }
    }
  }

  function buildRenderer() {
    if (S.renderer) { return; }
    var THREE = S.THREE;
    var sz = containerSize();
    S.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: false });
    S.renderer.setPixelRatio(Math.min((window.devicePixelRatio || 1), 2));
    S.renderer.setSize(sz.w, sz.h, false);
    S.canvas = S.renderer.domElement;
    S.canvas.setAttribute('role', 'img');
    S.canvas.setAttribute('aria-label', S.settings.loading);
    S.canvas.style.width = '100%';
    S.canvas.style.height = 'auto';
    S.canvas.style.maxWidth = '100%';

    S.scene = new THREE.Scene();
    try { S.scene.background = new THREE.Color(readBgColor()); } catch (e2) { S.scene.background = new THREE.Color('#ffffff'); }

    S.camera = new THREE.PerspectiveCamera(35, sz.w / sz.h, 0.01, 100);

    // [STUDIO ENV 2026-09-22] The prefiltered studio room goes on FIRST: the two
    // lights below are now trim on top of it, not the whole lighting budget. The
    // background stays the flat page colour set above, never the environment.
    applyStudioEnvironment(THREE);

    // Both lights lowered in 2026-09-22 now that scene.environment carries the fill.
    var hemi = new THREE.HemisphereLight(0xffffff, 0x707078, STUDIO_HEMI);
    S.scene.add(hemi);
    var key = new THREE.DirectionalLight(0xffffff, STUDIO_KEY);
    key.position.set(1.2, 2.0, 1.6);
    S.scene.add(key);

    S.root = new THREE.Group();
    S.scene.add(S.root);

    S.container.appendChild(S.canvas);
    buildPresets();
    buildCallouts();

    S.controls = new S.OrbitControls(S.camera, S.canvas);
    // [DESIGN REVIEW F8 2026-09-22] Orbit inertia (damping) keeps the model turning after release; it is off for
    // shoppers who ask the system for reduced motion, read once when the controls are built.
    S.controls.enableDamping = !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    S.controls.dampingFactor = 0.08;
    S.controls.addEventListener('start', function () { S.orbiting = true; });
    S.controls.addEventListener('end', function () { S.orbiting = false; });

    if (window.ResizeObserver) {
      S.ro = new ResizeObserver(function () { onResize(); });
      S.ro.observe(S.container);
    } else {
      window.addEventListener('resize', onResize);
    }

    S.built = true;
    startLoop();
  }

  function buildPresets() {
    var el = document.createElement('div');
    el.className = 'vmscfg-3d__presets';
    var front = document.createElement('button');
    front.type = 'button'; front.className = 'vmscfg-3d__preset'; front.textContent = S.settings.front;
    front.addEventListener('click', function () { setView(0, 8); });
    var side = document.createElement('button');
    side.type = 'button'; side.className = 'vmscfg-3d__preset'; side.textContent = S.settings.side;
    side.addEventListener('click', function () { setView(90, 8); });
    el.appendChild(front); el.appendChild(side);
    S.container.appendChild(el);
    S.presetsEl = el;
  }

  function buildCallouts() {
    var el = document.createElement('div');
    el.className = 'vmscfg-3d__callouts';
    S.calloutsEl = el;
    S.wrap.appendChild(el);
  }

  // ---- url resolution: which GLB each drawn object needs ---------------------
  // The plate uses the FOUR split files from #vmsw-config (never the rigid plate model_glb);
  // the pipe and head come from the pool (#vmscfg-data model_glb); the 11FC from the
  // companion JSON's model_glb. An empty url means "not on the store yet" and makes the
  // build "not available" (the plain sentence, nothing drawn).
  function splitPlateUrls(plateSku) {
    var cfg = S.config || {};
    if (plateSku === '11CP006') { return { base: cfg.plate_11CP006_base || '', swivel: cfg.plate_11CP006_swivel || '' }; }
    if (plateSku === 'ANGLE-CEILING') { return { base: cfg.plate_ANGLE_CEILING_base || '', swivel: cfg.plate_ANGLE_CEILING_swivel || '' }; }
    return { base: '', swivel: '' };
  }
  function poolUrl(sku) {
    var c = S.pool && S.pool[sku];
    return (c && hasModel(c.model_glb)) ? c.model_glb : '';
  }
  // [SLANTED 3D, HEADS SPLIT 2026-09-20] The split-head GLB urls from #vmsw-config for one of the
  // four pooled heads (else null). fixed + tilt for every head; 33CE008HEAD adds a threaded-collar
  // body. Keys use underscores. An empty string means the file is not on the store.
  function splitHeadUrls(headSku) {
    var cfg = S.config || {};
    if (headSku === '33HE001') { return { fixed: cfg.head_33HE001_fixed || '', tilt: cfg.head_33HE001_tilt || '' }; }
    if (headSku === '33HE002') { return { fixed: cfg.head_33HE002_fixed || '', tilt: cfg.head_33HE002_tilt || '' }; }
    if (headSku === '22HE001') { return { fixed: cfg.head_22HE001_fixed || '', tilt: cfg.head_22HE001_tilt || '' }; }
    if (headSku === '33CE008HEAD') {
      return { fixed: cfg.head_33CE008HEAD_fixed || '', tilt: cfg.head_33CE008HEAD_tilt || '',
        collar: cfg.head_33CE008HEAD_collar || '' };
    }
    return null;
  }
  // The head's split set is COMPLETE (so the theme poses the bodies and never fetches the rigid
  // model) only when every url in the set is non-empty. 33CE008HEAD requires the threaded collar too.
  function headSplitComplete(headSku, su) {
    if (!su) { return false; }
    if (headSku === '33CE008HEAD') { return hasModel(su.fixed) && hasModel(su.tilt) && hasModel(su.collar); }
    return hasModel(su.fixed) && hasModel(su.tilt);
  }
  function collarUrl() {
    var comp = S.config && S.config.companion;
    return (comp && hasModel(comp.model_glb)) ? comp.model_glb : '';
  }
  function collarSku() {
    var comp = S.config && S.config.companion;
    return (comp && comp.sku) ? comp.sku : null;
  }
  // Is the 11FC in the current build? getBuild().parts lists the companion SKU when it is.
  function has11fc(build) {
    if (!build || !Array.isArray(build.parts)) { return false; }
    var csku = collarSku();
    if (!csku) { return false; }
    for (var i = 0; i < build.parts.length; i++) {
      if (build.parts[i] && build.parts[i].sku === csku) { return true; }
    }
    return false;
  }

  // Resolve the { role -> url } plan for a scene/build. Returns { plan, urls, missing }.
  // [ROUND 4] The pipe/nipple url is model_glb, or, when that is blank, the 3A product-media GLB
  // (model_glb_3a) with a flag so placePose knows to synthesise its anchors from the mesh bbox.
  function planUrls(scene, build) {
    var plate = (build && build.plate) ? build.plate : {};
    var head = (build && build.head) ? build.head : {};
    var plateSku = plate.sku || null, headSku = head.sku || null;
    var sp = splitPlateUrls(plateSku);
    var pipeSku = findPipeSku(build);
    var pipeC = (pipeSku && S.pool) ? S.pool[pipeSku] : null;
    var pipeUrl = '', pipeIs3a = false, pipeLen = null, pipeSameLenSub = null;
    if (pipeC) {
      pipeLen = num(pipeC.nominal_length);
      // Tier 1: the part's OWN Class 3B model_glb (anchored). Tier 2: for a pipe with no model of
      // its own, the EXTENSION-KIT mapping (owner 2026-09-20) - a kit (10/20/30-inch-extension) is a
      // coupler PLUS a same-length fixed pipe, but the slanted engine models the mast as ONE fixed
      // pipe of the kit's nominal length with NO joint and NO coupler length (mastPiecesForBuild is
      // always a single piece), so draw the SAME-LENGTH fixed pipe's GLB (looked up in the pool,
      // never a hardcoded url) and NO coupler, matching the engine's geometry (residual still closes).
      // Tier 3: otherwise the part's own 3A product-media GLB (anchors synthesised from the bbox),
      // e.g. the close nipple 112-NPT-PIPE-Close which has no same-length peer. NOTE (finding): on
      // today's data the bare fixed pipes are cheaper than the kits and win the per-length price
      // dedup, so a kit is never actually selected here; tier 2 is the defensive path.
      if (hasModel(pipeC.model_glb)) { pipeUrl = pipeC.model_glb; }
      else {
        var sub = (pipeLen !== null) ? sameLengthPipeUrl(S.pool, pipeLen, pipeSku) : { url: '' };
        if (sub.url) { pipeUrl = sub.url; pipeSameLenSub = sub.sku; }
        else if (hasModel(pipeC.model_glb_3a)) { pipeUrl = pipeC.model_glb_3a; pipeIs3a = true; }
      }
    }
    var plan = {
      plate_base: sp.base, plate_swivel: sp.swivel,
      pipe: pipeUrl
    };
    // [SLANTED 3D, HEADS SPLIT 2026-09-20] Head plan (spec item 1). When the head is one of the four
    // pooled heads AND its split set is fully on the store, carry the split roles (head_fixed,
    // head_tilt, and head_collar for 33CE008HEAD) and DO NOT add the rigid head role, so the rigid
    // model_glb is never fetched here. Otherwise fall back to the rigid head, exactly as before.
    var su = splitHeadUrls(headSku);
    var headSplit = headSplitComplete(headSku, su);
    if (headSplit) {
      plan.head_fixed = su.fixed;
      plan.head_tilt = su.tilt;
      if (headSku === '33CE008HEAD') { plan.head_collar = su.collar; }
    } else {
      plan.head = poolUrl(headSku);
    }
    if (scene && scene.slides && has11fc(build) && num(scene.collarY) !== null) {
      plan.collar = collarUrl();
    }
    var urls = [], missing = [];
    for (var role in plan) {
      if (!plan.hasOwnProperty(role)) { continue; }
      var u = plan[role];
      if (!hasModel(u)) { missing.push(role); }
      else if (urls.indexOf(u) < 0) { urls.push(u); }
    }
    return { plan: plan, urls: urls, missing: missing, plateSku: plateSku, headSku: headSku,
      headSplit: headSplit,
      pipeIs3a: pipeIs3a, pipeLen: pipeLen, pipeSku: pipeSku, pipeSameLenSub: pipeSameLenSub };
  }

  // The pipe SKU for the current build. getBuild().parts holds the drawn part rows; the mast
  // is the row whose pool entry is a pipe OR a nipple - the page treats both as pipe stock
  // (vms-slanted-wall.js PIPES filter: component_type 'pipe' || 'nipple'), so a close-nipple
  // mast (112-NPT-PIPE-Close) is matched here too (and then found to have no model_glb, which
  // is the honest "not available" outcome, not a silently skipped pipe).
  function findPipeSku(build) {
    if (!build || !Array.isArray(build.parts)) { return null; }
    for (var i = 0; i < build.parts.length; i++) {
      var sku = build.parts[i] && build.parts[i].sku;
      var c = sku && S.pool ? S.pool[sku] : null;
      if (c && (c.component_type === 'pipe' || c.component_type === 'nipple')) { return sku; }
    }
    return null;
  }
  // The pool entry for the current pipe (for the telescoping length_min).
  function pipePoolEntry(build) {
    var sku = findPipeSku(build);
    return (sku && S.pool) ? S.pool[sku] : null;
  }

  // ---- read the anchor data off a loaded GLB (browser side) ------------------
  function anchorIn(clone, name) {
    var THREE = S.THREE;
    var o = clone.getObjectByName(name);
    if (!o) { return null; }
    var wp = new THREE.Vector3();
    o.getWorldPosition(wp);
    return [wp.x / IN_TO_M, wp.y / IN_TO_M, wp.z / IN_TO_M];
  }
  function anchorDirIn(clone, name) {
    // The hinge empty carries local +X along the pin. Read its world +X direction, in the
    // GLB's own frame (the clone is not yet rotated when this runs), as a unit vector.
    var THREE = S.THREE;
    var o = clone.getObjectByName(name);
    if (!o) { return null; }
    o.updateWorldMatrix(true, false);
    var m = o.matrixWorld;
    var x = new THREE.Vector3(m.elements[0], m.elements[1], m.elements[2]).normalize();
    return [x.x, x.y, x.z];
  }

  // Assemble the per-part anchor data computePose needs, from the loaded (un-posed) GLB
  // clones. Returns null for a role whose GLB is missing a required empty.
  function readParts(clones, plateSku) {
    var parts = {};
    if (clones.pipe) {
      var pt = anchorIn(clones.pipe, 'anchor_top'), pb = anchorIn(clones.pipe, 'anchor_bottom');
      if (pt && pb) { parts.pipe = { anchorTop: pt, anchorBottom: pb }; }
    }
    if (clones.plate_base && clones.plate_swivel) {
      var bh = anchorIn(clones.plate_base, 'hinge'), bd = anchorDirIn(clones.plate_base, 'hinge');
      var sh = anchorIn(clones.plate_swivel, 'hinge'), sab = anchorIn(clones.plate_swivel, 'anchor_bottom');
      var sd = anchorDirIn(clones.plate_swivel, 'hinge');
      if (bh && bd && sh && sab && sd) {
        parts.plate = {
          base: { hinge: bh, hingeDir: bd, rotationSign: (PLATE_ROTATION_SIGN[plateSku] || -1) },
          swivel: { hinge: sh, anchorBottom: sab, hingeDir: sd }
        };
      }
    }
    if (clones.head) {
      var ht = anchorIn(clones.head, 'anchor_top'), vc = anchorIn(clones.head, 'vesa_center');
      if (ht && vc) { parts.head = { anchorTop: ht, vesaCenter: vc }; }
    }
    // [SLANTED 3D, HEADS SPLIT 2026-09-20] The split-head bodies. FIXED carries anchor_top +
    // vesa_center, plus tilt_axis (single-axis heads) or collar_seat (33CE008HEAD). TILT carries
    // vesa_center at its origin, plus tilt_axis (single-axis). The threaded collar carries pipe_end.
    if (clones.head_fixed && clones.head_tilt) {
      var hfAT = anchorIn(clones.head_fixed, 'anchor_top'), hfVC = anchorIn(clones.head_fixed, 'vesa_center');
      var hfTA = anchorIn(clones.head_fixed, 'tilt_axis'), hfCS = anchorIn(clones.head_fixed, 'collar_seat');
      if (hfAT && hfVC) { parts.headFixed = { anchorTop: hfAT, vesaCenter: hfVC, tiltAxis: hfTA, collarSeat: hfCS }; }
      var htVC = anchorIn(clones.head_tilt, 'vesa_center'), htTA = anchorIn(clones.head_tilt, 'tilt_axis');
      if (htVC) { parts.headTilt = { vesaCenter: htVC, tiltAxis: htTA }; }
    }
    if (clones.head_collar) {
      var hcAT = anchorIn(clones.head_collar, 'anchor_top'), hcAB = anchorIn(clones.head_collar, 'anchor_bottom');
      var hcPE = anchorIn(clones.head_collar, 'pipe_end');
      if (hcPE) { parts.headCollar = { anchorTop: hcAT, anchorBottom: hcAB, pipeEnd: hcPE }; }
    }
    if (clones.collar) {
      var ct = anchorIn(clones.collar, 'anchor_top'), cb = anchorIn(clones.collar, 'anchor_bottom');
      if (ct) { parts.collar = { anchorTop: ct, anchorBottom: cb }; }
    }
    return parts;
  }

  // ---- repose: the scene -> the 3D assembly ----------------------------------
  function repose(gen) {
    if (gen === undefined) { gen = S.gen; }
    if (!S.renderer || !S.visible || S.gen !== gen) { return; }
    // [SLANTED 3D, ROUND 3 FIXES 2026-09-20] OWNER BUG A (stacked TV boxes / smeared head). Every
    // vmsw:rendered during a cold GLB load starts a repose with the SAME S.gen (gen only changes on
    // show/hide); loadGLB shares one in-flight promise per url, so all pending reposes resolve in the
    // same tick and each used to run placePose, stacking N object sets and N TV boxes that clearGroups
    // never removed. FIX: take a monotonic sequence at the top; after the load only the LATEST sequence
    // may place (stale ones return), placePose clears the groups itself immediately before placing, and
    // the scene/build are RE-READ at placement time (the latest state), not the snapshot the load began
    // with. clearGroups is no longer called up front for a drawable build, so the current frame stays on
    // screen (no flicker) until the new one is ready. The gen/visible/disposed guards are kept.
    var mySeq = ++S.reposeSeq;
    var scene = getScene();
    var build = getBuild();

    if (!scene || scene.ok !== true) {
      clearGroups();
      S.available = false; S.lastPose = null;
      showUnavailable();
      return;
    }
    var plan = planUrls(scene, build);
    if (plan.missing.length) {
      clearGroups();
      S.available = false;
      S.lastPose = { ok: false, reason: 'missing:' + plan.missing.join(',') };
      showUnavailable();
      return;
    }

    setStatus(S.settings.loading);
    S.awaitingFrame = true;
    if (S.canvas) { S.canvas.style.display = 'block'; }
    if (S.presetsEl) { S.presetsEl.style.display = 'flex'; }

    Promise.all(plan.urls.map(loadGLB)).then(function () {
      if (!S.visible || S.disposed || S.gen !== gen || !S.renderer) { return; }
      if (reposeSuperseded(mySeq, S.reposeSeq)) { return; }   // a newer repose owns the draw now
      var freshScene = getScene(), freshBuild = getBuild();   // re-read at placement time (latest state)
      if (!freshScene || freshScene.ok !== true) {
        clearGroups(); S.available = false; S.lastPose = null; showUnavailable(); return;
      }
      placePose(freshScene, freshBuild, plan);   // placePose clears the groups itself, then places
      S.loadedUrls = plan.urls.slice();
      updateCallouts(freshScene, freshBuild, true);
      updateAria(freshScene, freshBuild);
      fitCamera();
    }).catch(function (e) {
      if (S.gen !== gen || !S.visible || reposeSuperseded(mySeq, S.reposeSeq)) { return; }
      clearGroups();
      S.awaitingFrame = false;
      S.available = false;
      showUnavailable();
      if (S.container) { S.container.setAttribute('data-vmsw-3d-error', String(e && e.message ? e.message : e)); }
      try { console.warn('[vmsw-3d] ' + (e && e.message ? e.message : 'GLB load failed')); } catch (w) {}
    });
  }

  function showUnavailable() {
    setStatus(S.settings.unavailable);
    if (S.canvas) { S.canvas.style.display = 'none'; }
    if (S.presetsEl) { S.presetsEl.style.display = 'none'; }
    updateCallouts(getScene(), getBuild(), false);
    if (S.pendingOpenScroll && S.visible) { S.pendingOpenScroll = false; scrollCanvasToTopIfMobile(); }
  }

  function loadGLB(url) {
    if (S.glbCache[url]) { return Promise.resolve(S.glbCache[url]); }
    if (S.glbPromises[url]) { return S.glbPromises[url]; }
    S.glbPromises[url] = new Promise(function (resolve, reject) {
      S.loader.load(url, function (gltf) { S.glbCache[url] = gltf.scene; resolve(gltf.scene); }, undefined,
        function (err) {
          delete S.glbPromises[url];
          reject(new Error('GLB load failed: ' + url + (err && err.message ? ' (' + err.message + ')' : '')));
        });
    });
    return S.glbPromises[url];
  }

  // [SLANTED 3D] Extend a telescoping pipe clone's inner tube down the mast axis by extendIn
  // inches (solved minus length_min), so its anchor_bottom lands at the solved length. Same
  // node name ('tube_inner') and direction the configurator uses. No-op without the node.
  function applyTeleExtension(clone, extendIn) {
    var inner = clone.getObjectByName('tube_inner');
    if (inner) { inner.position.y = inner.position.y - extendIn * IN_TO_M; clone.updateMatrixWorld(true); }
  }

  // Build a group from a GLB url, apply the pose rotation, pin its anchor to the target.
  function placeObject(obj) {
    var THREE = S.THREE;
    var src = S.glbCache[obj.url];
    if (!src) { return null; }
    var clone = src.clone(true);
    if (obj.teleExtendIn) { applyTeleExtension(clone, obj.teleExtendIn); }
    var group = new THREE.Group();
    group.add(clone);
    S.root.add(group);
    // q = Rx(hingeRot) * Ry(yaw): yaw the file so the pin lies on X3, then rotate about the
    // pin (X3) by the signed surface angle. For every non-base object hingeRot is 0.
    var qy = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), (obj.yawDeg || 0) * Math.PI / 180);
    var qx = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), (obj.hingeRotDeg || 0) * Math.PI / 180);
    group.quaternion.copy(qx.multiply(qy));
    group.updateMatrixWorld(true);
    // Pin the anchor to the scene target (inches -> metres). Normally the anchor is a named GLB
    // empty; for a 3A-fallback pipe there is no empty, so pin by the bbox-synthesised anchorLocal
    // (transformed through the group matrix), which is identical for a plain fixed pipe.
    var a = clone.getObjectByName(obj.anchorName);
    var wp = new THREE.Vector3();
    var pinned = false;
    if (a) { a.getWorldPosition(wp); pinned = true; }
    else if (obj.anchorLocal) {
      wp.set(obj.anchorLocal[0] * IN_TO_M, obj.anchorLocal[1] * IN_TO_M, obj.anchorLocal[2] * IN_TO_M).applyMatrix4(group.matrixWorld);
      pinned = true;
    }
    if (pinned) {
      group.position.set(
        obj.anchorTarget[0] * IN_TO_M - wp.x,
        obj.anchorTarget[1] * IN_TO_M - wp.y,
        obj.anchorTarget[2] * IN_TO_M - wp.z
      );
      group.updateMatrixWorld(true);
    }
    countTris(group);
    return group;
  }

  function placePose(scene, build, plan) {
    var THREE = S.THREE;
    // [SLANTED 3D, ROUND 3 FIXES 2026-09-20] Clear the previous frame's groups + TV HERE, immediately
    // before placing, so a stacked-repose double placement is impossible by construction (owner bug A):
    // even if two placePose calls ever ran, the second clears the first's objects before adding its own.
    clearGroups();
    S.tris = 0;
    S.pipeSameLenSub = plan.pipeSameLenSub || null;
    // Load anchors from the un-posed clones first, so computePose sees real GLB numbers.
    var clones = {};
    var roleUrls = {
      plate_base: plan.plan.plate_base, plate_swivel: plan.plan.plate_swivel,
      pipe: plan.plan.pipe, head: plan.plan.head, collar: plan.plan.collar,
      head_fixed: plan.plan.head_fixed, head_tilt: plan.plan.head_tilt, head_collar: plan.plan.head_collar
    };
    var role;
    for (role in roleUrls) {
      if (!roleUrls.hasOwnProperty(role) || !hasModel(roleUrls[role])) { continue; }
      var src = S.glbCache[roleUrls[role]];
      if (src) { var c = src.clone(true); c.updateMatrixWorld(true); clones[role] = c; }
    }
    // [SLANTED 3D] Telescoping mast: let the inner tube out to the solved length BEFORE the
    // anchors are read, exactly as assets/vms-configurator-3d.js chains a tele (translate the
    // named 'tube_inner' node down by solved minus length_min, so anchor_bottom - which rides
    // the inner tube - lands at the solved length). scene.pipe is the solved length; length_min
    // comes from the pool. A fixed pipe or a tele GLB without a tube_inner node is left as is.
    // The same extension is stashed on the pose pipe object so the RENDER clone (placeObject
    // reclones from the cache) is extended identically to the anchor-read clone.
    var teleExtendIn = 0;
    if (scene.pipeKind === 'tele') {
      var pe = pipePoolEntry(build);
      var lmin = pe ? num(pe.length_min) : null;
      var solved = num(scene.pipe);
      if (lmin !== null && solved !== null) { teleExtendIn = solved - lmin; }
      if (clones.pipe && teleExtendIn) { applyTeleExtension(clones.pipe, teleExtendIn); }
    }
    // [ROUND 4] 3A pipe fallback: the product-media GLB has NO anchor empties, so synthesise
    // anchor_top / anchor_bottom from the mesh bounding box and VALIDATE the frame: the axis must
    // be centred (x, z within 0.06 in of the mast axis), the top within 0.06 in of y = 0 (already
    // the pipe-part frame), and the bbox height must equal the part's store length within 0.06 in.
    // Any failure -> not-available with reason '3a-frame-mismatch'.
    S.pipe3aUsed = false; S.pipe3aReason = null;
    var synthPipe = null;
    if (plan.pipeIs3a && clones.pipe) {
      var pbox = new THREE.Box3().setFromObject(clones.pipe);
      var syn = synth3aPipe({
        minX: pbox.min.x / IN_TO_M, maxX: pbox.max.x / IN_TO_M,
        minY: pbox.min.y / IN_TO_M, maxY: pbox.max.y / IN_TO_M,
        minZ: pbox.min.z / IN_TO_M, maxZ: pbox.max.z / IN_TO_M
      }, num(plan.pipeLen));
      if (syn.ok) { synthPipe = { anchorTop: syn.anchorTop, anchorBottom: syn.anchorBottom }; S.pipe3aUsed = true; }
      else { S.pipe3aReason = syn.reason; }
    }
    var parts = readParts(clones, plan.plateSku);
    if (plan.pipeIs3a) {
      if (synthPipe) { parts.pipe = synthPipe; }
      else {
        S.lastPose = { ok: false, reason: '3a-frame-mismatch' };
        S.residuals = {}; S.available = false; S.awaitingFrame = false; showUnavailable(); return;
      }
    }
    var pose = computePose(scene, build, parts);
    // Attach the tele extension to the pipe pose object for placeObject's render clone.
    if (teleExtendIn && pose && pose.objects) {
      for (var pi = 0; pi < pose.objects.length; pi++) {
        if (pose.objects[pi].role === 'pipe') { pose.objects[pi].teleExtendIn = teleExtendIn; }
      }
    }
    S.lastPose = pose;
    S.residuals = pose.residuals || {};
    S.available = pose.ok;
    if (!pose.ok) { S.awaitingFrame = false; showUnavailable(); return; }

    // Place each posed object by attaching its role url.
    var i;
    for (i = 0; i < pose.objects.length; i++) {
      var o = pose.objects[i];
      o.url = roleUrls[o.role] || roleUrls[o.role.replace('plate_', 'plate_')];
      if (o.role === 'plate_base') { o.url = roleUrls.plate_base; }
      else if (o.role === 'plate_swivel') { o.url = roleUrls.plate_swivel; }
      else { o.url = roleUrls[o.role]; }
      if (!hasModel(o.url)) { continue; }
      var g = placeObject(o);
      if (g) { S.groups.push(g); S.groupsByRole[o.role] = g; }
    }

    addTVBox(pose.tv);
  }

  // [SLANTED 3D, ROUND 3] Measure the ACTUAL mating gap between the posed 11FC top and the posed
  // head lower-block UNDERSIDE plane, from the real GLB anchors, so the smoke catches any anchor
  // surprise. The collar's top is its own 'anchor_top' empty; the head's block underside is its
  // 'anchor_top' (the seated pipe end) plus HEAD_BLOCK_UNDERSIDE_IN (measured). Returns inches, or
  // null when either is absent. Within 0.02 in whenever the 11FC is drawn.
  function collarMateGapMeasured() {
    if (!S.groupsByRole || !S.groupsByRole.collar || !S.THREE) { return null; }
    // [SLANTED 3D, HEADS SPLIT 2026-09-20] SPLIT path: measure the gap by POSED BOUNDING BOXES
    // (spec item 7, lesson of 2026-09-20: mate to mesh faces, verify by posed bounding boxes) -
    // the 11FC posed bbox TOP against the fixed body's near-axis underside (the lowest fixed-body
    // vertex within 1.2 in of the pipe axis, i.e. the collar_seat block-underside ring; the fixed
    // body's threaded ring and cap are removed, so the open bore leaves that ring as the lowest
    // near-axis geometry). ~0 when mated. RIGID path: the anchor method (11FC anchor_top minus the
    // head anchor_top plus the measured block-underside offset).
    if (S.groupsByRole.head_fixed) {
      var THREE = S.THREE;
      var cBox = new THREE.Box3().setFromObject(S.groupsByRole.collar);
      var collarTop = cBox.max.y / IN_TO_M;
      var underside = fixedBodyNearAxisUndersideY();
      if (underside === null) { return null; }
      return collarTop - underside;
    }
    if (!S.groupsByRole.head) { return null; }
    var cTop = groupAnchorWorldY(S.groupsByRole.collar, 'anchor_top');
    var hAnchor = groupAnchorWorldY(S.groupsByRole.head, 'anchor_top');
    if (cTop === null || hAnchor === null) { return null; }
    return cTop - (hAnchor + HEAD_BLOCK_UNDERSIDE_IN);
  }
  // [SLANTED 3D, HEADS SPLIT 2026-09-20] The lowest world Y of the posed FIXED body's vertices
  // within 1.2 in of the pipe (Y) axis - the near-axis block underside the 11FC top meets. null
  // when there is no fixed body or no such vertex.
  function fixedBodyNearAxisUndersideY() {
    var g = S.groupsByRole && S.groupsByRole.head_fixed;
    if (!g || !S.THREE) { return null; }
    var THREE = S.THREE, v = new THREE.Vector3(), minY = null;
    g.updateMatrixWorld(true);
    g.traverse(function (o) {
      if (!o.isMesh || !o.geometry || !o.geometry.attributes || !o.geometry.attributes.position) { return; }
      var pos = o.geometry.attributes.position, i, r, yIn;
      for (i = 0; i < pos.count; i++) {
        v.set(pos.getX(i), pos.getY(i), pos.getZ(i)).applyMatrix4(o.matrixWorld);
        r = Math.sqrt((v.x / IN_TO_M) * (v.x / IN_TO_M) + (v.z / IN_TO_M) * (v.z / IN_TO_M));
        if (r <= 1.2) { yIn = v.y / IN_TO_M; if (minY === null || yIn < minY) { minY = yIn; } }
      }
    });
    return minY;
  }
  // [SLANTED 3D, HEADS SPLIT 2026-09-20] The threaded collar's posed mesh top (bbox max Y, inches).
  function threadedCollarMeshTopY() {
    var g = S.groupsByRole && S.groupsByRole.head_collar;
    if (!g || !S.THREE) { return null; }
    return new S.THREE.Box3().setFromObject(g).max.y / IN_TO_M;
  }
  // [SLANTED 3D, HEADS SPLIT 2026-09-20] restGap MEASURED by posed bounding boxes (director review 2):
  // the fixed body's near-axis underside minus the threaded collar mesh top. 0 within 0.005 in at
  // rest (head bottom on the threaded collar); equals the slide distance when the head is slid up.
  function restGapMeasured() {
    var under = fixedBodyNearAxisUndersideY(), top = threadedCollarMeshTopY();
    if (under === null || top === null) { return null; }
    return under - top;
  }
  // [SLANTED 3D, HEADS SPLIT 2026-09-20] The pin residual, MEASURED from the posed empties. For a
  // single-axis head it is the distance between the two posed tilt_axis empties (fixed body and
  // tilt body), which the coincident-empty rule drives to ~0. For 33CE008HEAD (no tilt_axis) it is
  // the distance between the posed tilt body's vesa_center and its pose target. null when not split.
  function pinResidualMeasured() {
    if (!S.groupsByRole || !S.THREE) { return null; }
    var tiltG = S.groupsByRole.head_tilt, fixG = S.groupsByRole.head_fixed;
    if (!tiltG || !fixG) { return null; }
    var ta = groupAnchorWorldVec(tiltG, 'tilt_axis'), fa = groupAnchorWorldVec(fixG, 'tilt_axis');
    if (ta && fa) { return dist3(ta, fa); }
    var vc = groupAnchorWorldVec(tiltG, 'vesa_center');
    var tgt = (S.lastPose && S.lastPose.residuals && S.lastPose.residuals.tiltPinTargetWorld) || null;
    if (vc && tgt) { return dist3(vc, tgt); }
    return null;
  }
  function groupAnchorWorldY(group, name) {
    var o = group.getObjectByName(name);
    if (!o) { return null; }
    var wp = new S.THREE.Vector3();
    o.getWorldPosition(wp);
    return wp.y / IN_TO_M;
  }
  function groupAnchorWorldVec(group, name) {
    var o = group.getObjectByName(name);
    if (!o) { return null; }
    var wp = new S.THREE.Vector3();
    o.getWorldPosition(wp);
    return [wp.x / IN_TO_M, wp.y / IN_TO_M, wp.z / IN_TO_M];
  }
  function dist3(a, b) {
    var dx = a[0] - b[0], dy = a[1] - b[1], dz = a[2] - b[2];
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  // [SLANTED 3D, ROUND 3] A verification hook (not used by the page): read the POSED MESH
  // extents, not the anchors, so the smoke can confirm the 11FC actually mates the head block.
  // Returns inches: the 11FC mesh top/bottom (its Box3), the head mesh bottom (its Box3 min =
  // the threaded-collar ring bottom), the head block underside measured from the head's own
  // vertices (lowest world y among head verts with mast-axis radius 1.3 to 2.2 in in the block
  // region, head anchor plus -0.5 to 1.0 in), the mate gap (11FC top minus that block
  // underside) and how far the threaded ring pokes below the 11FC bottom.
  function meshDiagnostics() {
    if (!S.groupsByRole || !S.groupsByRole.collar || !S.groupsByRole.head || !S.THREE) { return null; }
    var THREE = S.THREE;
    var cBox = new THREE.Box3().setFromObject(S.groupsByRole.collar);
    var hBox = new THREE.Box3().setFromObject(S.groupsByRole.head);
    var collarTop = cBox.max.y / IN_TO_M, collarBottom = cBox.min.y / IN_TO_M;
    var headBottom = hBox.min.y / IN_TO_M;
    var hAnchor = groupAnchorWorldY(S.groupsByRole.head, 'anchor_top');
    // scan the head mesh for the block underside AND the threaded-collar ring bottom (world).
    // Block underside: mast-axis radius 1.3 to 2.2 in over head-relative y -0.5 to 1.0 in.
    // Threaded ring: radius 0.80 to 1.15 in over head-relative y -1.2 to 0.5 in (the narrow ring).
    var minBlockY = null, minRingY = null;
    var v = new THREE.Vector3();
    S.groupsByRole.head.updateMatrixWorld(true);
    S.groupsByRole.head.traverse(function (o) {
      if (!o.isMesh || !o.geometry || !o.geometry.attributes || !o.geometry.attributes.position) { return; }
      var pos = o.geometry.attributes.position, i, yIn, r, rel;
      for (i = 0; i < pos.count; i++) {
        if (hAnchor === null) { break; }
        v.set(pos.getX(i), pos.getY(i), pos.getZ(i)).applyMatrix4(o.matrixWorld);
        yIn = v.y / IN_TO_M;
        rel = yIn - hAnchor;                     // y relative to the head anchor
        r = Math.sqrt((v.x / IN_TO_M) * (v.x / IN_TO_M) + (v.z / IN_TO_M) * (v.z / IN_TO_M));
        if (rel >= -0.5 && rel <= 1.0 && r >= 1.3 && r <= 2.2) {
          if (minBlockY === null || yIn < minBlockY) { minBlockY = yIn; }
        }
        if (rel >= -1.2 && rel <= 0.5 && r >= 0.80 && r <= 1.15) {
          if (minRingY === null || yIn < minRingY) { minRingY = yIn; }
        }
      }
    });
    return {
      collarMeshTopY: collarTop, collarMeshBottomY: collarBottom,
      headMeshBottomY: headBottom, headAnchorY: hAnchor,
      headBlockUndersideMeshY: minBlockY,
      mateGapMeshIn: (minBlockY === null) ? null : (collarTop - minBlockY),
      threadedRingBottomMeshY: minRingY,
      // how far the threaded ring pokes below the 11FC bottom (positive = ring sticks out below)
      ringPokeBelowCollarIn: (minRingY === null) ? null : (collarBottom - minRingY),
      // the head's overall lowest mesh point below the 11FC (the double-stack lower arm, expected)
      headLowestBelowCollarIn: collarBottom - headBottom
    };
  }

  function countTris(obj) {
    obj.traverse(function (o) {
      if (o.isMesh && o.geometry) {
        var g = o.geometry;
        if (g.index) { S.tris += g.index.count / 3; }
        else if (g.attributes && g.attributes.position) { S.tris += g.attributes.position.count / 3; }
      }
    });
  }

  function addTVBox(tv) {
    if (!tv) { return; }
    var THREE = S.THREE;
    var TV_OPACITY = (S.settings && typeof S.settings.tvOpacity === 'number') ? S.settings.tvOpacity : 0.30;
    var tvTransparent = TV_OPACITY < 1;
    var geo = new THREE.BoxGeometry(tv.w * IN_TO_M, tv.h * IN_TO_M, tv.depth * IN_TO_M);
    var matBody = new THREE.MeshStandardMaterial({ color: 0x3a3a3f, roughness: 0.85, metalness: 0.0, transparent: tvTransparent, opacity: TV_OPACITY, depthWrite: !tvTransparent });
    var matFront = new THREE.MeshStandardMaterial({ color: 0x55555b, roughness: 0.8, metalness: 0.0, transparent: tvTransparent, opacity: TV_OPACITY, depthWrite: !tvTransparent });
    // Box face order: px, nx, py, ny, pz, nz. The screen faces the room (+Z3); lighter front.
    var mats = [matBody, matBody, matBody, matBody, matFront, matBody];
    var mesh = new THREE.Mesh(geo, mats);
    // Land the box where the 2D draws the TV: place the un-rotated centre, rotate about the X3
    // axis through the hole centre by Rx(+tiltDeg), THEN translate by the hole-centre
    // displacement (tv.hcDisp), the same rotate-then-translate the 2D uses (tvBox comment).
    // The pivot group carries pivot + hcDisp as its position and the rotation as its quaternion;
    // the mesh sits at centre - pivot (the ORIGINAL pivot) so the rotation is about the pivot.
    mesh.position.set(
      (tv.center[0] - tv.pivot[0]) * IN_TO_M,
      (tv.center[1] - tv.pivot[1]) * IN_TO_M,
      (tv.center[2] - tv.pivot[2]) * IN_TO_M
    );
    var pivot = new THREE.Group();
    pivot.position.set(
      (tv.pivot[0] + tv.hcDisp[0]) * IN_TO_M,
      (tv.pivot[1] + tv.hcDisp[1]) * IN_TO_M,
      (tv.pivot[2] + tv.hcDisp[2]) * IN_TO_M
    );
    pivot.add(mesh);
    if (tv.tiltDeg) {
      pivot.quaternion.setFromAxisAngle(new THREE.Vector3(1, 0, 0), tv.tiltSign * tv.tiltDeg * Math.PI / 180);
    }
    var edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(geo),
      new THREE.LineBasicMaterial({ color: 0x2a2a2f, transparent: true, opacity: 0.7 })
    );
    mesh.add(edges);
    S.root.add(pivot);
    S.tvMesh = pivot;
    S.tvFrontMat = matFront;
    countTris(pivot);
  }

  // ---- camera framing --------------------------------------------------------
  function contentBox() {
    var THREE = S.THREE;
    var box = new THREE.Box3();
    box.makeEmpty();
    for (var i = 0; i < S.groups.length; i++) { box.expandByObject(S.groups[i]); }
    if (S.tvMesh) { box.expandByObject(S.tvMesh); }
    return box;
  }

  var _fitDist = 1;
  function fitCamera() {
    var THREE = S.THREE;
    var box = contentBox();
    if (box.isEmpty()) { return; }
    var center = new THREE.Vector3(); box.getCenter(center);
    var size = new THREE.Vector3(); box.getSize(size);
    S.contentH_in = size.y / IN_TO_M;
    S._center = center;
    S._minTargetY = (box.min.y / IN_TO_M - 10) * IN_TO_M;
    applyCanvasSize();
    var maxDim = Math.max(size.x, size.y, size.z);
    var fov = S.camera.fov * Math.PI / 180;
    _fitDist = (maxDim / 2) / Math.tan(fov / 2) * 1.25 + maxDim * 0.15;
    S.controls.target.copy(center);
    setView(S.curAz, S.curEl);
  }

  function placeCameraAt(dx, dy, dz, d) {
    S.camera.position.set(S._center.x + d * dx, S._center.y + d * dy, S._center.z + d * dz);
    S.camera.lookAt(S._center);
    S.camera.updateMatrixWorld(true);
    S.camera.updateProjectionMatrix();
  }

  function maxCornerNdc(box) {
    if (!box || box.isEmpty() || !S.camera) { return 0; }
    var THREE = S.THREE;
    var xs = [box.min.x, box.max.x], ys = [box.min.y, box.max.y], zs = [box.min.z, box.max.z];
    var v = new THREE.Vector3();
    var m = 0, a, b, c, ax, ay;
    for (a = 0; a < 2; a++) {
      for (b = 0; b < 2; b++) {
        for (c = 0; c < 2; c++) {
          v.set(xs[a], ys[b], zs[c]).project(S.camera);
          ax = Math.abs(v.x); ay = Math.abs(v.y);
          if (ax > m) { m = ax; }
          if (ay > m) { m = ay; }
        }
      }
    }
    return m;
  }

  function setView(azimuthDeg, elevationDeg) {
    if (!S._center) { return; }
    S.curAz = azimuthDeg; S.curEl = elevationDeg;
    var az = azimuthDeg * Math.PI / 180, el = elevationDeg * Math.PI / 180;
    var dx = Math.cos(el) * Math.sin(az);
    var dy = Math.sin(el);
    var dz = Math.cos(el) * Math.cos(az);
    var box = contentBox();
    var d = _fitDist;
    var initial = _fitDist;
    var iter, m;
    for (iter = 0; iter < 4; iter++) {
      placeCameraAt(dx, dy, dz, d);
      m = maxCornerNdc(box);
      if (m > 0.92) { d = d * (m / 0.92); }
      else if (m < 0.80) {
        var shrunk = d * (m / 0.88);
        if (shrunk < 0.5 * initial) { shrunk = 0.5 * initial; }
        d = shrunk;
      } else { break; }
    }
    placeCameraAt(dx, dy, dz, d);
    S.controls.target.copy(S._center);
    S.controls.minDistance = 0.5 * d;
    S.controls.maxDistance = 3 * d;
    S.controls.update();
  }

  function refitCurrentView() { if (S._center) { setView(S.curAz, S.curEl); } }

  // ---- the render loop -------------------------------------------------------
  function startLoop() {
    function frame() {
      if (S.disposed) { return; }
      S.animId = requestAnimationFrame(frame);
      if (S.controls) {
        if (S._minTargetY !== undefined && S.controls.target.y < S._minTargetY) { S.controls.target.y = S._minTargetY; }
        S.controls.update();
      }
      if (S.renderer && S.scene && S.camera) {
        S.renderer.render(S.scene, S.camera);
        if (S.awaitingFrame && S.groups.length) {
          S.awaitingFrame = false;
          hideStatus();
          if (S.firstFrameAt === null) {
            S.firstFrameAt = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
          }
          if (S.pendingOpenScroll) { S.pendingOpenScroll = false; scrollCanvasToTopIfMobile(); }
        }
      }
    }
    S.animId = requestAnimationFrame(frame);
  }

  // ---- callouts and aria -----------------------------------------------------
  // Real-text callout strip under the canvas: the SAME figures the page already reports,
  // reusing the page's degree sign and inch shape. No new customer copy beyond the labels
  // (which are section settings copied from the configurator's defaults). No dimension
  // overlay running to a surface line (there is no surface).
  function fmtIn(v) { return (Math.round(v * 10) / 10).toFixed(1) + ' in'; }

  function calloutRow(label, value) {
    var row = document.createElement('span');
    row.className = 'vmscfg-3d__callout';
    var l = document.createElement('span'); l.className = 'vmscfg-3d__callout-label'; l.textContent = label + ':';
    var v = document.createElement('span'); v.className = 'vmscfg-3d__callout-value'; v.textContent = value;
    row.appendChild(l); row.appendChild(v);
    return row;
  }

  function updateCallouts(scene, build, available) {
    if (!S.calloutsEl) { return; }
    S.calloutsEl.textContent = '';
    var angle = (scene && num(scene.slopeDeg) !== null) ? (Math.round(scene.slopeDeg) + DEG_SIGN) : '-';
    var tilt = (scene && num(scene.tiltDeg) !== null) ? (Math.round(scene.tiltDeg) + DEG_SIGN) : '-';
    var pipe = (available && scene && num(scene.pipe) !== null) ? fmtIn(scene.pipe) : '-';
    var tv = (build && build.measurements_in && num(build.measurements_in.height) !== null)
      ? fmtIn(build.measurements_in.height).replace(' in', ' in tall') : '-';
    S.calloutsEl.appendChild(calloutRow(S.settings.angleLabel, angle));
    S.calloutsEl.appendChild(calloutRow(S.settings.tiltLabel, tilt));
    S.calloutsEl.appendChild(calloutRow(S.settings.pipeLabel, pipe));
    S.calloutsEl.appendChild(calloutRow(S.settings.tv, tv));
    S.calloutsMeasured = true;
  }

  function updateAria(scene, build) {
    if (!S.canvas) { return; }
    var angle = (scene && num(scene.slopeDeg) !== null) ? Math.round(scene.slopeDeg) : null;
    var label = '3D view of the slanted wall build';
    if (angle !== null) { label += ': ' + S.settings.angleLabel.toLowerCase() + ' ' + angle + ' degrees'; }
    if (scene && num(scene.pipe) !== null) { label += ', ' + S.settings.pipeLabel.toLowerCase() + ' ' + fmtIn(scene.pipe); }
    S.canvas.setAttribute('aria-label', label);
  }

  // ---- resize ----------------------------------------------------------------
  function onResize() {
    if (!S.renderer || !S.camera) { return; }
    applyCanvasSize();
    refitCurrentView();
  }

  // ---- disposal --------------------------------------------------------------
  // [SLANTED 3D, ROUND 2] The part GLBs are drawn as clone(true) of the cached source scenes,
  // and clone(true) SHARES geometry and materials with the source (three.js clones the node
  // graph, not the buffers). So on a re-pose or hide we must NOT dispose a cloned part's
  // geometry or materials - that would free buffers the cache still owns and corrupt the next
  // draw. We only REMOVE the part groups from the scene here; their shared geometry/materials
  // stay alive in S.glbCache. Only the TV box is module-owned (fresh geometry + materials each
  // pose), so it is the only thing whose geometry/materials we dispose.
  function clearGroups() {
    if (!S.root) { return; }
    var i;
    for (i = 0; i < S.groups.length; i++) { S.root.remove(S.groups[i]); }  // shared buffers: remove only
    S.groups = [];
    S.groupsByRole = {};
    if (S.tvMesh) { disposeObject(S.tvMesh); S.root.remove(S.tvMesh); S.tvMesh = null; S.tvFrontMat = null; }
    S.tris = 0;
    S.contentH_in = null;
    S.calloutsMeasured = false;
  }

  // Dispose a MODULE-OWNED object's geometries and materials (the TV box and its edge lines).
  // Never called on a cloned part group (its buffers are shared with the cache).
  function disposeObject(obj) {
    obj.traverse(function (o) {
      if (o.isMesh || o.isLine) {
        if (o.geometry && o.geometry.dispose) { o.geometry.dispose(); }
        if (o.material) {
          if (Array.isArray(o.material)) { o.material.forEach(function (m) { if (m && m.dispose) { m.dispose(); } }); }
          else if (o.material.dispose) { o.material.dispose(); }
        }
      }
    });
  }

  // [SLANTED 3D, ROUND 2] Cache lifecycle: hiding the view disposes the RENDERER, which frees
  // the WebGL context and every GPU buffer/texture with it, so no GPU memory leaks across a
  // hide. The parsed source scenes in S.glbCache are kept (CPU only) so re-showing is instant
  // (no refetch - it comes from the browser HTTP cache anyway - and no re-parse). The next
  // show() builds a fresh renderer that re-uploads the cached geometry to the new context. We
  // therefore never explicitly dispose the glbCache; it lives for the page's life. (Chosen
  // over "dispose the cache on hide and refetch" because the pool GLBs are small and instant
  // re-show is the better trade; verified 0 refetch on re-open in the smoke.)
  function disposeGL() {
    S.disposed = true;
    if (S.animId) { cancelAnimationFrame(S.animId); S.animId = 0; }
    if (S.reposeRaf) { cancelAnimationFrame(S.reposeRaf); S.reposeRaf = 0; }
    clearGroups();
    if (S.ro) { try { S.ro.disconnect(); } catch (e) {} S.ro = null; }
    if (S.controls && S.controls.dispose) { try { S.controls.dispose(); } catch (e2) {} }
    // [STUDIO ENV 2026-09-22] The prefiltered environment belongs to this renderer,
    // so it goes with it: drop the scene reference first, then dispose the texture.
    // The next buildRenderer builds a fresh one (never a second PMREM per renderer).
    if (S.scene) { try { S.scene.environment = null; } catch (eEnv) {} }
    if (S.envTex) { try { S.envTex.dispose(); } catch (eEnv2) {} }
    S.envTex = null; S.envMs = null;
    if (S.renderer) {
      try { S.renderer.dispose(); } catch (e3) {}
      if (S.canvas && S.canvas.parentNode) { S.canvas.parentNode.removeChild(S.canvas); }
    }
    if (S.presetsEl && S.presetsEl.parentNode) { S.presetsEl.parentNode.removeChild(S.presetsEl); }
    if (S.calloutsEl && S.calloutsEl.parentNode) { S.calloutsEl.parentNode.removeChild(S.calloutsEl); }
    S.renderer = null; S.scene = null; S.camera = null; S.controls = null; S.canvas = null;
    S.root = null; S.presetsEl = null; S.calloutsEl = null; S.built = false;
    S._center = null; S.awaitingFrame = false; S.pendingOpenScroll = false;
  }

  function dispose() { disposeGL(); }

  // ---- state() for the smoke test --------------------------------------------
  // [SLANTED 3D] Whether the scene holds any object that is NOT one of the drawn parts
  // (the posed GLB groups) or the TV box. This is the honest "no surface" check: we never
  // construct a ceiling/wall/slab, so the only children of S.root are S.groups and the TV
  // pivot. (A y-threshold probe like the flat-ceiling configurator's is WRONG here: on a
  // slope the real tilted plate flange legitimately rises above y = 0, which is a part, not
  // a surface.) Returns the count of unexpected root children; 0 means "parts only".
  function extraRootObjects() {
    if (!S.root) { return 0; }
    var known = S.groups.slice();
    if (S.tvMesh) { known.push(S.tvMesh); }
    var extra = 0;
    for (var i = 0; i < S.root.children.length; i++) {
      if (known.indexOf(S.root.children[i]) < 0) { extra++; }
    }
    return extra;
  }

  function currentCornersMaxNdc() {
    if (!S.camera || !S.THREE) { return null; }
    var box = contentBox();
    if (box.isEmpty()) { return null; }
    S.camera.updateMatrixWorld(true);
    return maxCornerNdc(box);
  }

  function state() {
    return {
      gen: S.gen, hasRenderer: !!S.renderer, awaitingFrame: S.awaitingFrame,
      visible: S.visible, disposed: S.disposed, built: S.built, bundleReady: S.bundleReady,
      available: S.available, loadedParts: S.loadedUrls.slice(), triangleCount: Math.round(S.tris),
      firstFrameAt: S.firstFrameAt, showClickAt: S.showClickAt,
      lastPose: S.lastPose, residuals: S.residuals,
      objectRoles: (S.lastPose && S.lastPose.objects) ? S.lastPose.objects.map(function (o) { return o.role; }) : [],
      // 0 means the scene holds ONLY the drawn parts and the TV, i.e. no surface object.
      extraRootObjects: extraRootObjects(),
      rootChildCount: (S.root ? S.root.children.length : 0),
      tvOpacity: (S.settings && typeof S.settings.tvOpacity === 'number') ? S.settings.tvOpacity : null,
      tvTransparent: (S.tvFrontMat ? !!S.tvFrontMat.transparent : null),
      canvasH_px: (typeof S.appliedCanvasH === 'number' ? S.appliedCanvasH : null),
      calloutsH_px: (S.calloutsEl ? S.calloutsEl.offsetHeight : null),
      cornersMaxNdc: currentCornersMaxNdc(),
      // The 11FC mating gap, MEASURED (split: 11FC posed bbox top minus the fixed body's near-axis
      // underside; rigid: collar anchor_top minus the head block underside). null when no 11FC drawn.
      collarMateGapMeasured: collarMateGapMeasured(),
      // [SLANTED 3D, HEADS SPLIT 2026-09-20] The split-head smoke fields (spec item 7).
      headSplit: !!(S.residuals && S.residuals.headSplit),
      headTiltDeg: (S.residuals && typeof S.residuals.headTiltDeg === 'number') ? S.residuals.headTiltDeg : null,
      // pin residual MEASURED from the posed empties (single-axis: the two tilt_axis empties; expect
      // under 0.001), and the pure-solver value for reference.
      pinResidualIn: pinResidualMeasured(),
      pinResidualSolver: (S.residuals && typeof S.residuals.pinResidualIn === 'number') ? S.residuals.pinResidualIn : null,
      threadedCollarTopY: (S.residuals && typeof S.residuals.threadedCollarTopY === 'number') ? S.residuals.threadedCollarTopY : null,
      collarSeatY: (S.residuals && typeof S.residuals.collarSeatY === 'number') ? S.residuals.collarSeatY : null,
      // [SLANTED 3D, HEADS SPLIT 2026-09-20] rest gap (director review 2): 0 at rest, = the slide
      // distance when slid. restGapIn from the pose; restGapMeasuredIn by posed bounding boxes.
      restGapIn: (S.residuals && typeof S.residuals.restGapIn === 'number') ? S.residuals.restGapIn : null,
      restGapMeasuredIn: restGapMeasured(),
      // [ROUND 4] whether the pipe used the 3A product-media GLB fallback (anchors synthesised
      // from the mesh bbox), and the reason if a 3A frame check failed.
      pipe3aUsed: !!S.pipe3aUsed,
      pipe3aReason: S.pipe3aReason || null,
      // [ROUND 4] the SKU whose GLB was substituted for a same-length extension kit, if any.
      pipeSameLenSub: S.pipeSameLenSub || null,
      // [STUDIO ENV 2026-09-22] ADDITIVE keys: whether the prefiltered studio
      // environment is actually on the scene, and the PMREM build cost in ms.
      studioEnv: !!(S.scene && S.scene.environment),
      studioEnvMs: S.envMs,
      // [DESIGN REVIEW F8 2026-09-22] ADDITIVE key: whether orbit damping (inertia) is on; false under
      // prefers-reduced-motion: reduce, null before the controls exist.
      damping: S.controls ? !!S.controls.enableDamping : null
    };
  }

  // ---- expose + boot ---------------------------------------------------------
  window.VMSSlanted3D = {
    computePose: computePose, parseTvOpacity: parseTvOpacity, canvasHeight: canvasHeight,
    synth3aPipe: synth3aPipe, sameLengthPipeUrl: sameLengthPipeUrl, ce008Displacement: ce008Displacement,
    headSplitComplete: headSplitComplete, reposeSuperseded: reposeSuperseded,
    init: init, show: show, hide: hide, dispose: dispose, state: state,
    meshDiagnostics: meshDiagnostics
  };

  function boot() {
    var wrap = document.querySelector('.vmsw-3d-wrap');
    if (wrap) { init(wrap.querySelector('#vmsw-elev3d') || wrap); }
  }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); }
  else { boot(); }
})();
